/**
 * Unified Tracking Manager for Journal Party
 * 
 * Handles client-side tracking for:
 * - Google Analytics 4 (GA4)
 * - Meta Pixel (Facebook/Instagram)
 * - TikTok Pixel
 * - PostHog (product analytics; see src/lib/analytics/posthog.ts)
 * - Server-side tracking via Supabase edge functions
 * 
 * Usage:
 *   import { trackEvent, trackPageView, trackConversion } from '@/utils/trackingManager';
 *   
 *   // Track custom event
 *   trackEvent('button_click', { button_name: 'get_started' });
 *   
 *   // Track conversion
 *   trackConversion('subscription_purchase', { value: 9.99, currency: 'USD' });
 */

import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import {
  buildGoogleAdsUserData,
  isJourneyFunnelOfferId,
  type GoogleAdsConversionKind,
  type GoogleAdsUserIdentity,
} from '@/utils/googleAdsConversions';
import {
  captureAcquisitionFromUrl,
  fbclidFromFbc,
  getAttributionIdentifiers,
  getAttributionUtmParams,
  getGa4AttributionParams,
  getFirstTouchAcquisitionContext,
  getSessionAcquisitionContext,
  getStoredAcquisitionContext,
  normalizeUrlForGa4Attribution,
  resolveAcquisitionFromUrl,
} from '@/utils/acquisitionAttribution';
import { getGA4ClientId, getGA4SessionId } from '@/utils/ga4Identity';
import { capturePostHogEvent } from '@/lib/analytics/posthog';
import {
  getAnalyticsExclusionReason,
  getCachedInstallEnvironment,
  isInternalAnalyticsBuild,
  isInternalAnalyticsDevice,
  loadInstallEnvironment,
  markInternalAnalyticsDevice,
} from '@/utils/analyticsBuildEnvironment';
import {
  NATIVE_ANALYTICS_EXPLICIT_GRANT_KEY,
  getProductAnalyticsStatus,
  hasExplicitAnalyticsGrant,
  hasProductAnalyticsConsent,
  hasProductAnalyticsOptOut,
  persistNativeAnalyticsExplicitGrant,
  pushAccountAnalyticsOptOut,
} from '@/utils/productAnalyticsConsent';

export { hasExplicitAnalyticsGrant, hasProductAnalyticsConsent, hasProductAnalyticsOptOut };
import {
  getNativeAppsFlyerId,
  logNativeAppsFlyerEvent,
} from '@/utils/nativeAppsFlyerAttribution';
import {
  setNativeFirebaseAnalyticsUserId,
  syncNativeFirebaseAnalyticsConsent,
  trackNativeFirebaseAnalyticsEvent,
} from '@/utils/nativeFirebaseAnalytics';
import {
  sanitizeAnalyticsEventData,
  sanitizeSensitiveUrl,
  isCurrentPageAdExcluded,
} from '@/utils/urlPrivacy';
import { freePageAttribution } from '@/utils/freePageTracking';
import { buildSignupAttributionPayload } from '@/utils/persistUserAcquisition';
import {
  getCachedAccountAdDataOptOut,
  pushAccountAdDataOptOut,
  resolveAccountAdDataOptOut,
  startAccountAdDataSyncRetry,
  syncDeviceAdDataOptOutToAccount,
  type AdDataOptOutSource,
} from '@/utils/adDataAccountSync';

// Type definitions
interface ConversionData {
  value?: number;
  currency?: string;
  transaction_id?: string;
  items?: Array<{
    item_id: string;
    item_name: string;
    price?: number;
    quantity?: number;
  }>;
  [key: string]: unknown;
}

interface MarketingIdentifiers {
  fbp?: string;
  fbc?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  ttclid?: string;
  ttp?: string;
  /**
   * GA4 client_id from the _ga cookie. Required for server-side Measurement
   * Protocol events to attribute to the same GA4 user as client-side events.
   * Null when GA4 is blocked (ad blocker, consent denied) or not loaded.
   */
  gaClientId?: string;
  /**
   * GA4 session_id from the _ga_<measurement_id> cookie. Optional: used to
   * associate server-side MP events with the exact GA4 session.
   */
  gaSessionId?: string;
  /**
   * AppsFlyer Web SDK first-party cookie id. Used by AppsFlyer Web S2S to
   * associate backend Stripe/PayPal conversions with prior web visits.
   */
  appsflyerWebUserId?: string;
  userAgent?: string;
  clientIp?: string;
}

export interface CheckoutAttributionPayload {
  /** First-touch landing URL. */
  sourceUrl?: string;
  /** The page this checkout runs on; the server sends it to Meta as event_source_url. */
  pageUrl?: string;
  fbclid?: string;
  /** The latest Meta ad click's campaign, ad set and ad ids (digits only). */
  metaCampaignId?: string;
  metaAdsetId?: string;
  metaAdId?: string;
  fbp?: string;
  fbc?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  ttclid?: string;
  ttp?: string;
  gaClientId?: string;
  gaSessionId?: string;
  userAgent?: string;
  utmParams?: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    utm_id?: string;
  };
  acquisition?: {
    network?: string;
    source?: string;
    medium?: string;
    campaign?: string;
    adGroup?: string;
    ad?: string;
    creative?: string;
    keyword?: string;
  };
  /** Ad consent at checkout time; the server stores it for webhook sends. */
  ad_consent?: TrackingConsentStatus;
  gpc?: boolean;
  opt_in_jurisdiction?: boolean;
}

type FanoutMode = 'none' | 'destinations';
type FanoutTarget = 'meta' | 'ga4' | 'tiktok';

export type SkippablePlatform = 'meta' | 'tiktok' | 'ga4' | 'google_ads' | 'appsflyer' | 'posthog';

/**
 * Journal Party Live reminder funnel event names. trackEvent accepts any
 * string; these constants keep the client and the Edge Function senders
 * (which capture live_reminder_sent server-side) on the same spelling.
 */
export const LIVE_REMINDER_TRACKING_EVENTS = {
  /** A Remind Me tap (props: channel, surface, guest). */
  tapped: 'live_remind_tapped',
  /** The series reminder was saved (Phase 1 name, kept). */
  set: 'live_reminder_set',
  /** A signed-out visitor tapped Remind Me (Phase 1 name, kept). */
  guestTap: 'live_reminder_guest_tap',
  /** The series reminder was turned off (props: surface). */
  turnedOff: 'live_reminder_turned_off',
  /** Server-side only: a reminder push or email went out. */
  sent: 'live_reminder_sent',
  /** A reminder push was opened (props: touch, event_id). */
  opened: 'live_reminder_opened',
} as const;

export type LiveReminderTrackingEvent =
  (typeof LIVE_REMINDER_TRACKING_EVENTS)[keyof typeof LIVE_REMINDER_TRACKING_EVENTS];

interface TrackEventOptions {
  serverOnly?: boolean;
  clientOnly?: boolean;
  userId?: string;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
  /** Google Ads enhanced conversions only: buyer phone, postal code, country. */
  userPhone?: string;
  userZip?: string;
  userCountry?: string;
  /**
   * The Journey funnel sale this conversion is (front-end purchase, Premium
   * upsell, trial). Google Ads sends it to that sale's own conversion action
   * once the action is configured and params.offer_id is a journey_* offer;
   * otherwise today's label is used.
   */
  googleAdsConversion?: GoogleAdsConversionKind;
  /** Email sign_up only: the secret stored on the new account at signUp. */
  signupProof?: string;
  eventId?: string;
  fanoutMode?: FanoutMode;
  fanoutTargets?: FanoutTarget[];
  sourceUrl?: string;
  /** Skip specific client-side platforms (e.g. when server-side CAPI handles them). */
  skipPlatforms?: readonly SkippablePlatform[];
  /**
   * Native billing conversions can update SKAN conversion values without sharing
   * ATT-gated identifiers. Keep this opt-in and do not use it for web/PBA events.
   */
  allowNativeAppsFlyerWithoutConsent?: boolean;
  /**
   * Server first: track-event records the event before any client
   * destination fires. When the server reports a duplicate (an account's
   * second sign_up) nothing is sent client-side; when the server could not
   * be asked (no ad consent on web, network error) only PostHog runs
   * client-side, because ad and GA4 destinations cannot be deduped.
   */
  serverFirst?: boolean;
  /**
   * Server first only: told how track-event answered. 'not_sent' means the
   * server was not asked (no ad consent on web), 'failed' that it was asked
   * and did not store the event (503, network error).
   */
  onServerAnswer?: (answer: ServerFirstAnswer) => void;
}

export type ServerFirstAnswer = 'stored' | 'deduped' | 'failed' | 'not_sent';

/** Client destinations that count conversions and cannot dedupe a repeat. */
const AD_AND_GA4_PLATFORMS: readonly SkippablePlatform[] = [
  'ga4',
  'meta',
  'google_ads',
  'tiktok',
  'appsflyer',
];

interface PageViewOptions {
  skipPlatforms?: readonly SkippablePlatform[];
  pageCategory?: 'admin' | 'app' | 'public' | 'funnel' | 'technical';
  isAdminPage?: boolean;
}

export const TRACKING_CONSENT_EVENT = 'jp:tracking-consent-changed';
export const SHARED_TRACKING_CONSENT_COOKIE = 'jp_tracking_consent';
export type TrackingConsentStatus = 'pending' | 'granted' | 'denied';
const TRACKING_CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const NATIVE_TRACKING_CONSENT_KEY = 'jp_native_tracking_consent';
const NATIVE_APP_TRACKING_OPTOUT_KEY = 'jp_native_app_tracking_opt_out';
/**
 * Holds the user id whose account ad data opt-out
 * (user_privacy_settings.ad_data_opt_out) was applied to this device. Ads
 * only: product analytics does not read it. It applies while that member (or
 * nobody yet) is signed in, so signing out keeps it; another member's sign-in
 * ignores it and reads their own account. An explicit Accept on this device
 * clears it (except on a GPC browser).
 */
export const ACCOUNT_AD_DATA_OPT_OUT_KEY = 'jp_account_ad_data_opt_out';
/** How long sign-in waits for the account opt-out before skipping ad identity setup. */
const AD_ACCOUNT_READ_TIMEOUT_MS = 2000;
const GA4_LOAD_RETRY_MS = 250;
const GA4_LOAD_RETRY_LIMIT = 20;
// Only the current page may be recovered after an initial consent choice.
// Keep it in memory; never persist or replay a visitor's pre-consent history.
let pendingGa4PageView: { params: Record<string, unknown>; eventId?: string } | null = null;
const ga4RetryTimers = new Set<number>();
let ga4TrackingEpoch = 0;

function clearPendingGa4Tracking(): void {
  ga4TrackingEpoch += 1;
  pendingGa4PageView = null;
  if (typeof window !== 'undefined') {
    ga4RetryTimers.forEach(timer => window.clearTimeout(timer));
  }
  ga4RetryTimers.clear();
}
const GA4_EVENT_CONTEXT_PARAM_ALIASES = {
  source: 'event_source',
  medium: 'event_medium',
  campaign: 'event_campaign',
  term: 'event_term',
  content: 'event_content',
} as const;
const GOOGLE_ADS_LOAD_RETRY_MS = 250;
const GOOGLE_ADS_LOAD_RETRY_LIMIT = 20;
const GOOGLE_ADS_PENDING_CONVERSIONS_KEY = 'jp:google_ads:pending_conversions';
const META_LOAD_RETRY_MS = 250;
const META_LOAD_RETRY_LIMIT = 20;
const META_PIXEL_EVENT_STATE_PREFIX = 'jp:meta_pixel_event:';
const META_PIXEL_PENDING_TTL_MS = META_LOAD_RETRY_MS * (META_LOAD_RETRY_LIMIT + 2);
const META_CAPI_PIXEL_WAIT_MS = META_PIXEL_PENDING_TTL_MS;
const META_CAPI_PIXEL_WAIT_POLL_MS = 50;
const CLIENT_CONVERSION_DEDUPE_PREFIX = 'jp:client_conversion:';
const CLIENT_CONVERSION_DEDUPE_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const PAGE_VIEW_DEDUPE_WINDOW_MS = 15_000;
const PAGE_VIEW_SESSION_DEDUPE_WINDOW_MS = 60_000;
const FUNNEL_PAGE_VIEW_PERSISTENT_DEDUPE_WINDOW_MS = 24 * 60 * 60 * 1000;
const PAGE_VIEW_SESSION_DEDUPE_PREFIX = 'jp:page_view:';
const FUNNEL_PAGE_VIEW_PERSISTENT_DEDUPE_PREFIX = 'jp:funnel_page_view:';
/** Drop rapid repeat `checkout_started` / `initiate_checkout` for the same attempt. */
const CHECKOUT_STARTED_DEDUPE_WINDOW_MS = 5_000;
const CHECKOUT_STARTED_SESSION_DEDUPE_PREFIX = 'jp:checkout_started:';
const SERVER_TRACKING_SESSION_ID_KEY = 'jp:tracking_session_id';
const NATIVE_APPSFLYER_ID_CACHE_MS = 5 * 60 * 1000;
const NATIVE_APPSFLYER_ID_MISSING_CACHE_MS = 10 * 1000;
const APPSFLYER_ATT_PENDING_EVENTS_KEY = 'jp:appsflyer:att_pending_events';
const APPSFLYER_ATT_PENDING_EVENTS_MAX = 50;
const APPSFLYER_ATT_FLUSH_DELAY_MS = 1500;
const INTERNAL_PAGE_VIEW_SKIP_PLATFORMS: readonly SkippablePlatform[] = [
  'ga4',
  'meta',
  'google_ads',
  'tiktok',
  'posthog',
];
const APPSFLYER_EVENT_MAP: Record<string, string> = {
  sign_up: 'af_complete_registration',
  login: 'af_login',
  onboarding_completed: 'onboarding_completed',
  first_session_started: 'first_session_started',
  first_session_completed: 'first_session_completed',
  checkout_started: 'af_initiated_checkout',
  trial_started: 'af_start_trial',
  subscription_purchase: 'af_subscribe',
  checkout_canceled: 'checkout_canceled',
  checkout_failed: 'checkout_failed',
  view_content: 'af_content_view',
  powerprompt_started: 'powerprompt_started',
  powerprompt_completed: 'powerprompt_completed',
  journal_prompt_completed: 'journal_prompt_completed',
  program_started: 'program_started',
  program_completed: 'program_completed',
  challenge_day_completed: 'challenge_day_completed',
  challenge_completed: 'challenge_completed',
  event_joined: 'event_joined',
  event_completed: 'event_completed',
  content_shared: 'af_share',
  streak_achieved: 'af_achievement_unlocked',
  badge_earned: 'af_achievement_unlocked',
};

// Declare global window types for tracking pixels
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    ttq?: {
      identify?: (identity: { email?: string; phone_number?: string; external_id?: string }) => void;
      /** TikTok dedupes pixel and Events API copies on options.event_id. */
      track?: (eventName: string, params?: Record<string, unknown>, options?: { event_id?: string }) => void;
    };
    AF?: ((plugin: 'pba', method: string, payload?: unknown) => void) & {
      q?: unknown[];
      id?: unknown;
      plugins?: Record<string, unknown>;
    };
    __JP_META_USER_DATA__?: {
      em?: string;
      external_id?: string;
    };
    __JP_TIKTOK_USER_DATA__?: {
      email?: string;
      external_id?: string;
    };
    dataLayer?: unknown[];
    __JP_META_PIXEL_IDS__?: string[];
    __JP_META_ADVANCED_MATCHING_INIT_KEYS__?: Record<string, string>;
    __JP_GA4_MEASUREMENT_IDS__?: string[];
    __JP_GOOGLE_ADS_CONFIGS__?: Array<{
      conversionId: string;
      conversionLabel: string | null;
      checkoutConversionLabel: string | null;
      leadConversionLabel: string | null;
      trialConversionLabel: string | null;
      purchaseConversionLabel: string | null;
      subscriptionPurchaseConversionLabel: string | null;
      /** Set only when the kind's label AND server action id are configured. */
      journeyPurchaseConversionLabel?: string | null;
      journeyUpsellConversionLabel?: string | null;
      journeyTrialConversionLabel?: string | null;
    }>;
    /**
     * ISO 3166-1 alpha-2 country code injected at SSR time from the
     * `x-vercel-ip-country` header. `null` means Vercel did not resolve a
     * country (we treat that as the safest default: opt-in). See
     * `api/ssr.ts` for the injection site.
     */
    __JP_GEO_COUNTRY__?: string | null;
  }
}

// Must stay in sync with `CONSENT_OPT_IN_COUNTRIES` in `api/ssr.ts`.
// These jurisdictions require explicit opt-in before any analytics or
// advertising tracking fires (GDPR / UK-GDPR / nFADP / LGPD).
const CONSENT_OPT_IN_COUNTRIES = new Set<string>([
  // EU member states
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE',
  // EEA (non-EU)
  'IS', 'LI', 'NO',
  // UK (post-Brexit still under UK-GDPR / PECR)
  'GB',
  // Switzerland (nFADP)
  'CH',
  // Brazil (LGPD)
  'BR',
]);

/**
 * Set on `/` by a vercel.json header rule from `x-vercel-ip-country`. The
 * homepage is the prerendered static dist/index.html, which never passes
 * through api/ssr.ts, so it has no `__JP_GEO_COUNTRY__`.
 */
export const GEO_COUNTRY_COOKIE = 'jp_geo_country';

function normalizeGeoCountry(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim().toUpperCase();
  // XX is Vercel's placeholder for an unresolved country.
  return /^[A-Z]{2}$/.test(trimmed) && trimmed !== 'XX' ? trimmed : null;
}

function readSsrGeoCountry(): string | null {
  if (typeof window === 'undefined') return null;
  // A server-injected value, even null, is authoritative for the page.
  if (window.__JP_GEO_COUNTRY__ !== undefined) return normalizeGeoCountry(window.__JP_GEO_COUNTRY__);
  try {
    return normalizeGeoCountry(readCookie(GEO_COUNTRY_COOKIE));
  } catch {
    return null;
  }
}

/**
 * Detect the Global Privacy Control signal.
 *
 * Per the GPC spec and California CPRA regulations, a browser-sent GPC
 * signal MUST be treated as a verified opt-out of the sale or sharing of
 * personal information, without any additional user confirmation. We
 * surface this as `denied` so every downstream tracking guard refuses to
 * fire and no CPRA "sell / share" signal leaves the device.
 */
export function hasGlobalPrivacyControlSignal(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true;
}

/**
 * Check whether the current visitor is in a jurisdiction that requires
 * explicit opt-in consent before tracking. Reads the SSR-injected country
 * code, or on the static homepage the geo cookie -- unknown countries are
 * treated as opt-in (safest default).
 */
export function isConsentOptInJurisdiction(): boolean {
  const country = readSsrGeoCountry();
  if (!country) return true; // Unknown -> safest default is opt-in.
  return CONSENT_OPT_IN_COUNTRIES.has(country);
}

/**
 * Resolve the initial tracking consent default for a visitor who has not
 * yet made an explicit choice. Returns:
 *
 * - `denied`  -- when the browser sends a Global Privacy Control signal
 *   (honored per CPRA regardless of jurisdiction).
 * - `pending` -- when the visitor is in an opt-in jurisdiction (EU/EEA
 *   /UK/CH/BR) or the country could not be resolved. The cookie banner
 *   renders and tracking stays blocked until the user accepts.
 * - `granted` -- when the visitor is in an opt-out jurisdiction (US and
 *   elsewhere outside the opt-in set). CCPA/CPRA allows implicit consent
 *   paired with a persistent "Your Privacy Choices" opt-out link;
 *   tracking fires immediately.
 *
 * This only runs when there is no persisted explicit choice. As soon as
 * the visitor clicks Accept, Decline, or opts out via the CPRA dialog,
 * the stored value takes precedence.
 */
export function resolveInitialConsentDefault(): TrackingConsentStatus {
  if (hasGlobalPrivacyControlSignal()) {
    return 'denied';
  }
  if (isConsentOptInJurisdiction()) {
    return 'pending';
  }
  return 'granted';
}

function createTrackingEventId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `jp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * The same UUID for the same event and account on every device (SHA-256 of
 * `eventName:userId`, shaped as a version 5 style UUID). PostHog dedupes on
 * it, Meta and TikTok dedupe browser and server copies on it, and
 * conversion_events has a unique index on event_id. Falls back to a random id
 * where Web Crypto is unavailable.
 */
export async function createAccountScopedEventId(
  eventName: string,
  userId: string
): Promise<string> {
  try {
    const digest = new Uint8Array(
      await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${eventName}:${userId}`))
    );
    const bytes = digest.slice(0, 16);
    bytes[6] = (bytes[6] & 0x0f) | 0x50;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  } catch {
    return createTrackingEventId();
  }
}

export function minutesSinceSignup(createdAt: string | null | undefined, now: Date | number = Date.now()): number {
  const createdAtMs = typeof createdAt === 'string' ? Date.parse(createdAt) : NaN;
  const nowMs = now instanceof Date ? now.getTime() : now;

  if (!Number.isFinite(createdAtMs) || !Number.isFinite(nowMs)) {
    return 0;
  }

  return Math.max(0, Math.floor((nowMs - createdAtMs) / 60_000));
}

export function getServerTrackingSessionId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    const existing = window.sessionStorage.getItem(SERVER_TRACKING_SESSION_ID_KEY);
    if (existing) return existing;

    const next = createTrackingEventId();
    window.sessionStorage.setItem(SERVER_TRACKING_SESSION_ID_KEY, next);
    return next;
  } catch {
    return createTrackingEventId();
  }
}

// The session id rotates per app launch (sessionStorage), but install-to-
// revenue measurement joins on the session id the native_app_install event
// carried. Persist that id durably at first-open so later checkouts can
// attach the ORIGINAL install key even after restarts.
const NATIVE_INSTALL_SESSION_ID_KEY = 'jp_native_install_session_id';

export function persistNativeInstallSessionId(): void {
  if (typeof window === 'undefined') return;

  try {
    if (window.localStorage.getItem(NATIVE_INSTALL_SESSION_ID_KEY)) return;
    const sessionId = getServerTrackingSessionId();
    if (sessionId) {
      window.localStorage.setItem(NATIVE_INSTALL_SESSION_ID_KEY, sessionId);
    }
  } catch {
    // Ignore storage failures; attribution falls back to the live session id.
  }
}

/**
 * Install key for attribution: the durably stored first-open session id when
 * present, else the live session id (pre-existing installs that never stored
 * one; their linkage relies on the user-id join instead).
 */
export function getInstallSessionIdForAttribution(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    const stored = window.localStorage.getItem(NATIVE_INSTALL_SESSION_ID_KEY);
    if (stored) return stored;
  } catch {
    // Fall through to the live session id.
  }
  return getServerTrackingSessionId();
}

/**
 * Check if user has consented to tracking. This is the advertising and
 * attribution gate (GA4, Meta, TikTok, Google Ads, AppsFlyer, web server
 * tracking). On iOS it follows App Tracking Transparency. First-party product
 * analytics uses hasProductAnalyticsConsent() instead.
 */
export function hasTrackingConsent(): boolean {
  return getTrackingConsentStatus() === 'granted';
}


function isNativeRuntime(): boolean {
  return Capacitor.isNativePlatform();
}

function isNativeIosRuntime(): boolean {
  return isNativeRuntime() && Capacitor.getPlatform() === 'ios';
}

function isNativeAndroidRuntime(): boolean {
  return isNativeRuntime() && Capacitor.getPlatform() === 'android';
}

function getNativeStoreForCurrentPlatform(): string | null {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'app_store';
  if (platform === 'android') return 'play_store';
  return null;
}

let nativeAppsFlyerIdCache:
  | {
      value: string | null;
      readAt: number;
    }
  | null = null;
let nativeAppsFlyerIdPromise: Promise<string | null> | null = null;

/** Test seam: forget the cached native AppsFlyer id between tests. */
export function resetNativeAppsFlyerIdCacheForTests(): void {
  nativeAppsFlyerIdCache = null;
  nativeAppsFlyerIdPromise = null;
}

async function getCachedNativeAppsFlyerId(): Promise<string | null> {
  if (!isNativeRuntime()) return null;

  const now = Date.now();
  const cacheWindowMs = nativeAppsFlyerIdCache?.value
    ? NATIVE_APPSFLYER_ID_CACHE_MS
    : NATIVE_APPSFLYER_ID_MISSING_CACHE_MS;
  if (nativeAppsFlyerIdCache && now - nativeAppsFlyerIdCache.readAt < cacheWindowMs) {
    return nativeAppsFlyerIdCache.value;
  }

  if (!nativeAppsFlyerIdPromise) {
    nativeAppsFlyerIdPromise = getNativeAppsFlyerId()
      .catch((error) => {
        if (import.meta.env.DEV) {
          console.warn('Failed to read native AppsFlyer id for server tracking:', error);
        }
        return null;
      })
      .finally(() => {
        nativeAppsFlyerIdPromise = null;
      });
  }

  const value = await nativeAppsFlyerIdPromise;
  nativeAppsFlyerIdCache = { value, readAt: now };
  return value;
}

async function enrichNativeServerEventData(eventData: Record<string, unknown>): Promise<void> {
  if (!isNativeRuntime()) return;

  const platform = Capacitor.getPlatform();
  if (eventData.is_native == null) eventData.is_native = true;
  if (eventData.native_platform == null) eventData.native_platform = platform;
  if (eventData.store == null) {
    const store = getNativeStoreForCurrentPlatform();
    if (store) eventData.store = store;
  }
  const installEnvironment = getCachedInstallEnvironment();
  if (installEnvironment) {
    if (eventData.install_channel == null) eventData.install_channel = installEnvironment.channel;
    if (eventData.app_version == null && installEnvironment.appVersion) {
      eventData.app_version = installEnvironment.appVersion;
    }
    if (eventData.app_build == null && installEnvironment.appBuild) {
      eventData.app_build = installEnvironment.appBuild;
    }
  }

  if (eventData.appsflyer_id != null || eventData.appsflyerId != null) return;

  const appsflyerId = await getCachedNativeAppsFlyerId();
  if (!appsflyerId) return;

  eventData.appsflyer_id = appsflyerId;
  if (eventData.native_attribution_source == null) {
    eventData.native_attribution_source = 'native_appsflyer_plugin';
  }
  if (eventData.native_attribution_captured_at == null) {
    eventData.native_attribution_captured_at = new Date().toISOString();
  }
}

function getSharedCookieDomain(): string | null {
  if (typeof window === 'undefined') return null;

  const { hostname } = window.location;
  if (hostname === 'journalparty.com' || hostname.endsWith('.journalparty.com')) {
    return '.journalparty.com';
  }

  return null;
}

function writeCookie(
  name: string,
  value: string,
  options?: {
    domain?: string | null;
    maxAge?: number;
  }
): void {
  if (typeof document === 'undefined') return;

  const parts = [`${name}=${encodeURIComponent(value)}`, 'path=/', 'SameSite=Lax'];
  if (typeof options?.maxAge === 'number') {
    parts.push(`max-age=${options.maxAge}`);
  }
  if (options?.domain) {
    parts.push(`domain=${options.domain}`);
  }
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    parts.push('Secure');
  }

  document.cookie = parts.join('; ');
}

function persistSharedTrackingConsent(status: 'granted' | 'denied'): void {
  writeCookie(SHARED_TRACKING_CONSENT_COOKIE, status, {
    domain: getSharedCookieDomain(),
    maxAge: TRACKING_CONSENT_COOKIE_MAX_AGE,
  });
}

function clearCookie(name: string, domain?: string | null): void {
  writeCookie(name, '', { domain, maxAge: 0 });
}

function persistLegacyTrackingConsent(status: 'granted' | 'denied'): void {
  if (typeof window === 'undefined') return;

  const legacyConsent = status === 'granted' ? 'accepted' : 'declined';

  try {
    window.localStorage.setItem('tracking_consent', status);
    window.localStorage.setItem('cookie_consent_status', legacyConsent);
  } catch {
    // Ignore storage failures (private mode / browser restrictions).
  }
}

function persistNativeTrackingConsent(status: TrackingConsentStatus): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(NATIVE_TRACKING_CONSENT_KEY, status);
  } catch {
    // Ignore storage failures (private mode / browser restrictions).
  }
}

function persistNativeAppTrackingOptOut(optedOut: boolean): void {
  if (typeof window === 'undefined') return;

  try {
    if (optedOut) {
      window.localStorage.setItem(NATIVE_APP_TRACKING_OPTOUT_KEY, '1');
    } else {
      window.localStorage.removeItem(NATIVE_APP_TRACKING_OPTOUT_KEY);
    }
  } catch {
    // Ignore storage failures; event gating fails closed when the preference
    // cannot be read.
  }
}

/** The member signed in on this page (null until a sign-in is seen). */
let activeAdIdentityUserId: string | null = null;

function readAccountAdDataOptOutOwner(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const owner = window.localStorage.getItem(ACCOUNT_AD_DATA_OPT_OUT_KEY);
    return owner && owner !== '1' ? owner : null;
  } catch {
    return null;
  }
}

function hasAccountAdDataOptOut(): boolean {
  const owner = readAccountAdDataOptOutOwner();
  if (!owner) return false;
  return activeAdIdentityUserId === null || activeAdIdentityUserId === owner;
}

function persistAccountAdDataOptOut(ownerUserId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (ownerUserId) window.localStorage.setItem(ACCOUNT_AD_DATA_OPT_OUT_KEY, ownerUserId);
    else window.localStorage.removeItem(ACCOUNT_AD_DATA_OPT_OUT_KEY);
  } catch {
    // Storage blocked: the next sign-in reads the account again.
  }
}

/** Test hook: forget which member this page has seen sign in. */
export function resetAdIdentityUserForTests(): void {
  activeAdIdentityUserId = null;
}

/**
 * Forgets every ad identity this page or device handed to ad platforms: the
 * Meta and TikTok advanced matching caches, Meta's manual advanced matching
 * (set to empty), the AppsFlyer Web customer user id, the GA4 / Firebase
 * user_id, and the stored tracking user id/email. Run when the signed-in member changes or signs out, and when
 * the ad data policy does not allow the member's identity.
 *
 * Limits: Meta's pixel and TikTok's pixel keep identify data they already
 * received in memory until the page reloads (neither documents a reset), so
 * this stops passing user data rather than recalling it; TikTok identify is
 * simply not called again. AppsFlyer documents no unset for the Web SDK
 * customer user id; an empty value is sent as the best available reset.
 */
export function clearAdIdentityState(): void {
  if (typeof window === 'undefined') return;
  delete window.__JP_META_USER_DATA__;
  delete window.__JP_TIKTOK_USER_DATA__;
  delete window.__JP_META_ADVANCED_MATCHING_INIT_KEYS__;
  try {
    window.fbq?.('set', 'userData', {});
  } catch {
    // The pixel may not be ready; nothing cached is passed from here on.
  }
  try {
    window.AF?.('pba', 'setCustomerUserId', '');
  } catch {
    // Web SDK not ready.
  }
  // GA4 / Firebase user_id, set by configureAdIdentity.
  if (isNativeRuntime()) {
    void setNativeFirebaseAnalyticsUserId(null);
  } else {
    try {
      window.gtag?.('set', { user_id: null });
    } catch {
      // gtag not ready.
    }
    // Google Ads enhanced conversions data set by trackToGoogleAds, and the
    // buyer details on conversions still waiting for the tag.
    clearGoogleAdsUserData();
    stripPendingGoogleAdsConversionIdentity();
  }
  clearStoredTrackingUserIdentifiers();
}

function readStoredTrackingUserId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('tracking_user_id');
  } catch {
    return null;
  }
}

/**
 * Sign-out on this page: the previous member's ad identity is cleared. Their
 * applied account opt-out marker stays (it keeps applying while nobody is
 * signed in).
 */
export function noteAdIdentitySignedOut(): void {
  if (activeAdIdentityUserId === null && !readStoredTrackingUserId()) return;
  const previousEffectiveStatus = getTrackingConsentStatus();
  activeAdIdentityUserId = null;
  clearAdIdentityState();
  const nextEffectiveStatus = getTrackingConsentStatus();
  if (previousEffectiveStatus !== nextEffectiveStatus) notifyTrackingConsent(nextEffectiveStatus);
}

/** Records who is signed in; another member's applied opt-out stops applying. */
function noteActiveAdIdentityUser(userId: string): void {
  if (!userId || activeAdIdentityUserId === userId) return;
  // A different member than the one whose identity this device holds (this
  // page, or a stored tracking id from an earlier visit): forget it first.
  const storedUserId = readStoredTrackingUserId();
  if (
    (activeAdIdentityUserId !== null && activeAdIdentityUserId !== userId) ||
    (storedUserId !== null && storedUserId !== userId)
  ) {
    clearAdIdentityState();
  }
  const previousEffectiveStatus = getTrackingConsentStatus();
  activeAdIdentityUserId = userId;
  const nextEffectiveStatus = getTrackingConsentStatus();
  if (previousEffectiveStatus !== nextEffectiveStatus) notifyTrackingConsent(nextEffectiveStatus);
}

function hasNativeAppTrackingOptOut(): boolean {
  if (typeof window === 'undefined') return false;
  // The account's ad opt-out counts as the app's opt-out on every device.
  if (hasAccountAdDataOptOut()) return true;

  try {
    return window.localStorage.getItem(NATIVE_APP_TRACKING_OPTOUT_KEY) === '1';
  } catch {
    // Do not send to advertising partners when the opt-out state is unknown.
    return true;
  }
}

function clearStoredTrackingUserIdentifiers(): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem('tracking_user_id');
    window.localStorage.removeItem('tracking_user_email');
  } catch {
    // Ignore storage failures (private mode / browser restrictions).
  }
}

export function clearTrackingConsentPersistence(): void {
  clearPendingGa4Tracking();
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem('tracking_consent');
      window.localStorage.removeItem('cookie_consent_status');
      window.localStorage.removeItem('tracking_user_id');
      window.localStorage.removeItem('tracking_user_email');
      window.localStorage.removeItem(NATIVE_APP_TRACKING_OPTOUT_KEY);
      window.localStorage.removeItem(NATIVE_TRACKING_CONSENT_KEY);
      window.localStorage.removeItem(NATIVE_ANALYTICS_EXPLICIT_GRANT_KEY);
    } catch {
      // Ignore storage failures (private mode / browser restrictions).
    }
  }

  clearPendingAppsFlyerAttEvents();
  clearPendingGoogleAdsConversions();
  clearCookie(SHARED_TRACKING_CONSENT_COOKIE, getSharedCookieDomain());
}

/**
 * Read persisted tracking consent status.
 *
 * Order of precedence:
 * 1. Explicit user choice (shared cookie, localStorage, legacy banner key).
 * 2. `resolveInitialConsentDefault()` -- GPC signal, then jurisdiction.
 *
 * Opt-in jurisdictions (EU/EEA/UK/CH/BR) and unknown-country visitors
 * fall through to `pending` so the cookie banner renders and nothing
 * fires until they accept. US-and-elsewhere visitors implicitly resolve
 * to `granted` under the CCPA/CPRA opt-out model, paired with the
 * "Your Privacy Choices" link exposed in the footer.
 */
function getWebTrackingConsentStatus(): TrackingConsentStatus {
  if (typeof window === 'undefined') return 'pending';

  // GPC always wins for this browser, even over an earlier Accept (CPRA:
  // the signal is a valid opt-out of sale/sharing for this browser).
  if (hasGlobalPrivacyControlSignal()) return 'denied';
  // The account's ad opt-out (applied at sign-in) beats an earlier Accept.
  if (hasAccountAdDataOptOut()) return 'denied';

  try {
    const sharedConsent = readCookie(SHARED_TRACKING_CONSENT_COOKIE);
    if (sharedConsent === 'granted' || sharedConsent === 'denied') {
      persistLegacyTrackingConsent(sharedConsent);
      return sharedConsent;
    }

    const trackingConsent = window.localStorage.getItem('tracking_consent');
    if (trackingConsent === 'granted' || trackingConsent === 'denied') {
      persistSharedTrackingConsent(trackingConsent);
      return trackingConsent;
    }

    // Backward-compatible fallback for the cookie banner status key.
    const cookieConsent = window.localStorage.getItem('cookie_consent_status');
    if (cookieConsent === 'accepted') {
      persistLegacyTrackingConsent('granted');
      persistSharedTrackingConsent('granted');
      return 'granted';
    }
    if (cookieConsent === 'declined') {
      persistLegacyTrackingConsent('denied');
      persistSharedTrackingConsent('denied');
      return 'denied';
    }

    // No explicit user choice -- resolve against GPC + jurisdiction.
    return resolveInitialConsentDefault();
  } catch {
    return resolveInitialConsentDefault();
  }
}

function getNativeTrackingConsentStatus(): TrackingConsentStatus {
  if (typeof window === 'undefined') return 'pending';

  try {
    const trackingConsent = window.localStorage.getItem(NATIVE_TRACKING_CONSENT_KEY);
    if (trackingConsent === 'granted' || trackingConsent === 'denied' || trackingConsent === 'pending') {
      return trackingConsent;
    }
  } catch {
    return 'pending';
  }

  return 'pending';
}

function getNativeAndroidTrackingConsentStatus(): TrackingConsentStatus {
  if (hasGlobalPrivacyControlSignal()) return 'denied';
  if (hasAccountAdDataOptOut()) return 'denied';

  if (typeof window !== 'undefined') {
    try {
      const nativeConsent = window.localStorage.getItem(NATIVE_TRACKING_CONSENT_KEY);
      if (nativeConsent === 'granted' || nativeConsent === 'denied') return nativeConsent;

      const trackingConsent = window.localStorage.getItem('tracking_consent');
      if (trackingConsent === 'granted' || trackingConsent === 'denied') return trackingConsent;

      const cookieConsent = window.localStorage.getItem('cookie_consent_status');
      if (cookieConsent === 'accepted') return 'granted';
      if (cookieConsent === 'declined') return 'denied';
    } catch {
      return 'pending';
    }
  }

  return 'granted';
}

export function getTrackingConsentStatus(): TrackingConsentStatus {
  if (isNativeIosRuntime()) {
    return hasNativeAppTrackingOptOut() ? 'denied' : getNativeTrackingConsentStatus();
  }
  if (isNativeAndroidRuntime()) return getNativeAndroidTrackingConsentStatus();

  return getWebTrackingConsentStatus();
}

/** The explicit ad opt-out in the app (Your Privacy Choices / Android toggle / GPC). */
function hasNativeAdDataOptOut(): boolean {
  if (isNativeIosRuntime()) return hasNativeAppTrackingOptOut();
  return getNativeAndroidTrackingConsentStatus() === 'denied';
}

export interface AdConsentRequestFields {
  ad_consent: TrackingConsentStatus;
  gpc: boolean;
  /** Omitted when unknown (native apps have no geo country). */
  opt_in_jurisdiction?: boolean;
}

/**
 * The ad consent snapshot the server stores with a checkout or event, so
 * webhook-time and fanout sends resolve the same ad data policy as this
 * browser (supabase/functions/_shared/adDataPolicy.ts):
 * - web: the effective consent, GPC, and whether the visitor is in an
 *   opt-in jurisdiction (unknown country counts as opt-in, as in the banner).
 * - native: only the explicit ad opt-out counts ('denied'); ATT is Apple's
 *   separate gate (sent as advertiser_tracking_enabled), so anything else is
 *   'pending' and the region is left unknown.
 */
export function getAdConsentRequestFields(): AdConsentRequestFields {
  if (isNativeRuntime()) {
    return {
      ad_consent: hasNativeAdDataOptOut() ? 'denied' : 'pending',
      gpc: hasGlobalPrivacyControlSignal(),
    };
  }
  return {
    ad_consent: getTrackingConsentStatus(),
    gpc: hasGlobalPrivacyControlSignal(),
    opt_in_jurisdiction: isConsentOptInJurisdiction(),
  };
}

/**
 * A web visitor who opted out (Decline, Your Privacy Choices, GPC) outside an
 * opt-in jurisdiction. Browser pixels stay off, but conversions may still go
 * to Meta and TikTok from the server in Limited Data Use mode, which US
 * state laws allow (the platforms act as service providers). In an opt-in
 * jurisdiction a decline means nothing is shared, so this is false there.
 */
export function isLimitedServerAdMode(): boolean {
  if (isNativeRuntime()) return false;
  return getTrackingConsentStatus() === 'denied' && !isConsentOptInJurisdiction();
}

/**
 * Conversion events the client asks the server to send under Limited Data
 * Use when its pixels are off. Trial, subscription and purchase events are
 * webhook-owned (see BusinessEvents) and page views are left out.
 */
const LIMITED_SERVER_FANOUT_EVENTS: ReadonlySet<string> = new Set([
  'lead',
  'sign_up',
  'view_content',
  'add_to_cart',
  'checkout_started',
  'initiate_checkout',
  'add_payment_info',
]);

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function createMetaBrowserId(): string {
  const timestamp = Date.now();
  let randomValue = '';

  try {
    const randomBytes = new Uint32Array(2);
    crypto.getRandomValues(randomBytes);
    randomValue = `${randomBytes[0]}${randomBytes[1]}`;
  } catch {
    randomValue = Math.floor(Math.random() * 1_000_000_000_000_000_000).toString();
  }

  return `fb.1.${timestamp}.${randomValue}`;
}

function ensureMetaBrowserId(): string | null {
  if (typeof document === 'undefined') return null;
  if (!hasTrackingConsent()) return null;

  const existing = readCookie('_fbp');
  if (existing) return existing;

  const next = createMetaBrowserId();
  writeCookie('_fbp', next, {
    domain: getSharedCookieDomain(),
    maxAge: TRACKING_CONSENT_COOKIE_MAX_AGE,
  });
  return next;
}

// Mint and PERSIST the Meta click id (_fbc) the same way we persist _fbp.
// fbclid only appears on the ad-landing URL, so the previous in-memory-only
// reconstruction (`fb.1.${Date.now()}.${fbclid}`) rebuilt a fresh timestamp on
// every call and vanished once the user navigated -- so the conversion events
// that actually matter for Event Match Quality (trial, subscribe) usually
// carried no fbc. Writing _fbc once, with a stable creation time, means every
// later event sends the SAME click id and the browser pixel reads it natively.
//
// A new ad click (an fbclid that differs from the one in _fbc) replaces the
// cookie, stamped with this click's time, so Meta credits the latest ad
// rather than one clicked up to a year earlier.
function ensureMetaClickId(): string | null {
  if (typeof document === 'undefined') return null;
  if (!hasTrackingConsent()) return null;

  const fbclid =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('fbclid')?.trim() || null
      : null;
  const existing = readCookie('_fbc');
  if (existing && (!fbclid || fbclidFromFbc(existing) === fbclid)) return existing;

  const stored = getAttributionIdentifiers().fbc;
  // Meta format: fb.<subdomainIndex>.<creationTimeMs>.<fbclid>. Index 1 for an
  // apex/second-level domain. Reuse an already-captured value for this click
  // verbatim so its original creation time is preserved.
  const storedForThisClick = stored && (!fbclid || fbclidFromFbc(stored) === fbclid) ? stored : null;
  const next = storedForThisClick || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : null);
  if (!next) return null;

  writeCookie('_fbc', next, {
    domain: getSharedCookieDomain(),
    maxAge: TRACKING_CONSENT_COOKIE_MAX_AGE,
  });
  return next;
}

function getMarketingIdentifiers(): MarketingIdentifiers {
  let storedIdentifiers = getAttributionIdentifiers();
  if (typeof window === 'undefined') return storedIdentifiers;

  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get('fbclid')?.trim() || null;

  // Capture a new ad click before reading the stored click id.
  if (fbclid && fbclidFromFbc(storedIdentifiers.fbc) !== fbclid) {
    captureAcquisitionFromUrl(window.location.href);
    storedIdentifiers = getAttributionIdentifiers();
  }

  const fbp = readCookie('_fbp') || ensureMetaBrowserId() || undefined;
  const fbc = ensureMetaClickId() || storedIdentifiers.fbc || undefined;
  const gclid = params.get('gclid') || storedIdentifiers.gclid;
  const gbraid = params.get('gbraid') || storedIdentifiers.gbraid;
  const wbraid = params.get('wbraid') || storedIdentifiers.wbraid;
  const ttclid = params.get('ttclid') || storedIdentifiers.ttclid;
  const ttp = readCookie('_ttp') || undefined;
  const appsflyerWebUserId = readCookie('afUserId') || readCookie('afUserid') || undefined;

  // GA4 cookie identifiers. Captured here so every tracked event persists
  // the _ga client_id server-side, enabling the Stripe webhook (and other
  // edge functions) to fire server-side Measurement Protocol events that
  // attribute to the correct GA4 user session.
  // Native events belong to Firebase app streams. Never forward a legacy
  // WebView _ga cookie into web Measurement Protocol attribution.
  const gaClientId = isNativeRuntime() ? undefined : getGA4ClientId() ?? undefined;
  const gaSessionId = isNativeRuntime() ? undefined : getGA4SessionId() ?? undefined;

  return {
    fbp,
    fbc,
    gclid,
    gbraid,
    wbraid,
    ttclid,
    ttp,
    gaClientId,
    gaSessionId,
    appsflyerWebUserId,
  };
}

function getMergedUtmParams(): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
} | undefined {
  if (typeof window === 'undefined') return getAttributionUtmParams();
  const touch = resolveAcquisitionFromUrl(window.location.href);
  return touch ? {
    utm_source: touch.utm_source || undefined,
    utm_medium: touch.utm_medium || undefined,
    utm_campaign: touch.utm_campaign || undefined,
    utm_term: touch.utm_term || undefined,
    utm_content: touch.utm_content || undefined,
  } : undefined;
}

function buildServerEventData(params?: Record<string, unknown>): Record<string, unknown> {
  const eventData: Record<string, unknown> = { ...(params || {}), ...getAcquisitionMeasurementParams() };
  const acquisition = getStoredAcquisitionContext();

  if (eventData.session_id == null) {
    const sessionId = getServerTrackingSessionId();
    if (sessionId) eventData.session_id = sessionId;
  }

  if (eventData.platform == null) {
    eventData.platform = isNativeRuntime() ? Capacitor.getPlatform() : 'web';
  }
  eventData.analytics_exclusion_reason = getAnalyticsExclusionReason() ?? 'none';

  if (eventData.tracking_consent_status == null) {
    eventData.tracking_consent_status = getTrackingConsentStatus();
  }

  // Ad consent snapshot for the server's ad data policy (adDataPolicy.ts).
  if (eventData.ad_consent == null) {
    const adConsent = getAdConsentRequestFields();
    eventData.ad_consent = adConsent.ad_consent;
    eventData.ad_gpc = adConsent.gpc;
    if (adConsent.opt_in_jurisdiction !== undefined) {
      eventData.ad_opt_in_region = adConsent.opt_in_jurisdiction;
    }
  }

  // Product analytics controls read by track-event before forwarding to
  // PostHog: an explicit opt-out (not an ATT decline) and internal builds.
  eventData.analytics_opt_out = hasProductAnalyticsOptOut();
  eventData.is_internal_build = isInternalAnalyticsBuild();

  if (acquisition) {
    const ga4Attribution = getGa4AttributionParams(acquisition);
    if (eventData.acquisition_network == null) {
      eventData.acquisition_network = acquisition.network;
    }
    if (eventData.acquisition_source == null) {
      eventData.acquisition_source = acquisition.utm_source || acquisition.network;
    }
    if (eventData.acquisition_medium == null) {
      eventData.acquisition_medium = acquisition.utm_medium;
    }
    if (eventData.acquisition_campaign == null) {
      eventData.acquisition_campaign = acquisition.utm_campaign || acquisition.campaign;
    }
    if (eventData.acquisition_ad_group == null) {
      eventData.acquisition_ad_group = acquisition.ad_group;
    }
    if (eventData.acquisition_ad == null) {
      eventData.acquisition_ad = acquisition.ad;
    }
    if (eventData.acquisition_creative == null) {
      eventData.acquisition_creative = acquisition.creative || acquisition.utm_content;
    }
    if (eventData.acquisition_keyword == null) {
      eventData.acquisition_keyword = acquisition.keyword || acquisition.utm_term;
    }
    if (eventData.acquisition_ga4_source == null) {
      eventData.acquisition_ga4_source = ga4Attribution.acquisition_source;
    }
    if (eventData.acquisition_ga4_medium == null) {
      eventData.acquisition_ga4_medium = ga4Attribution.acquisition_medium;
    }
    if (eventData.acquisition_paid_social_placement == null) {
      eventData.acquisition_paid_social_placement = ga4Attribution.paid_social_placement;
    }
    if (eventData.paid_social_placement == null) {
      eventData.paid_social_placement = ga4Attribution.paid_social_placement;
    }
  }

  return eventData;
}

/** Explicit observed touches alongside the unchanged latest-touch compatibility fields. */
function getAcquisitionMeasurementParams(): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const safeLabel = (value: string | null | undefined): string | null => {
    const text = value?.trim();
    // Campaign labels are operator-controlled, but URLs and identifiers have
    // no place in these product measurement dimensions.
    return text && text.length <= 160 && !/[@?&#=]|https?:\/\//i.test(text) ? text : null;
  };
  for (const [prefix, touch] of [
    ['first_touch', getFirstTouchAcquisitionContext()],
    ['session_entry', getSessionAcquisitionContext()],
  ] as const) {
    if (!touch) continue;
    result[`${prefix}_source`] = safeLabel(touch.utm_source || touch.network);
    result[`${prefix}_medium`] = safeLabel(touch.utm_medium);
    result[`${prefix}_campaign`] = safeLabel(touch.utm_campaign || touch.campaign);
    result[`${prefix}_captured_at`] = touch.captured_at;
    if (touch.landing_path) {
      try {
        const path = new URL(touch.landing_path, 'https://journalparty.com').pathname;
        result[`${prefix}_landing_path`] = /@|%40/i.test(path) ? null : sanitizeTrackingUrl(path, 'path');
      } catch {
        result[`${prefix}_landing_path`] = null;
      }
    }
  }
  if (getFirstTouchAcquisitionContext()) result.first_touch_provenance = 'first_observed_since_rollout';
  if (getSessionAcquisitionContext()) result.session_entry_semantics = 'tab_route_capture_30m_inactivity';
  return result;
}

function hasXmlTagPathContamination(pathname: string): boolean {
  const candidates = [pathname];
  try {
    candidates.push(decodeURIComponent(pathname));
  } catch {
    // Keep the raw candidate when the path is not valid percent-encoding.
  }

  return candidates.some((candidate) => /<\/(?:loc|url|urlset|sitemapindex)>/i.test(candidate));
}

/** The current page as an absolute URL with payment references and auth tokens removed. */
function getCurrentPageTrackingUrl(): string | undefined {
  return typeof window !== 'undefined' ? sanitizeTrackingUrl(window.location.href, 'absolute') : undefined;
}

function sanitizeTrackingUrl(rawUrl: string, output: 'absolute' | 'path'): string {
  try {
    const privacySafeUrl = sanitizeSensitiveUrl(rawUrl, output);
    const url = new URL(privacySafeUrl, 'https://journalparty.local');
    url.hash = '';
    if (hasXmlTagPathContamination(url.pathname)) {
      url.pathname = '/404';
      url.search = '';
    }
    const query = url.searchParams.toString();

    if (output === 'path') {
      return `${url.pathname}${query ? `?${query}` : ''}`;
    }

    return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`;
  } catch {
    return rawUrl.split('#')[0] || rawUrl;
  }
}

function sanitizeTrackingSourceUrl(rawUrl: string | undefined): string | undefined {
  if (!rawUrl) return undefined;

  const privacySafeUrl = sanitizeSensitiveUrl(rawUrl, 'preserve');
  try {
    new URL(privacySafeUrl, 'https://journalparty.local');
    return privacySafeUrl;
  } catch {
    return undefined;
  }
}

function getTrackingSourceUrl(): string | undefined {
  const stored = getStoredAcquisitionContext();
  if (stored?.source_url) return sanitizeTrackingUrl(stored.source_url, 'absolute');
  if (typeof window !== 'undefined') return sanitizeTrackingUrl(window.location.href, 'absolute');
  return undefined;
}

export function isLocalWebTrackingEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  if (import.meta.env.MODE === 'test') return false;
  if (isNativeRuntime()) return false;

  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

export function getCheckoutAttributionPayload(): CheckoutAttributionPayload {
  const identifiers = getMarketingIdentifiers();
  const utmParams = getMergedUtmParams();
  const acquisition = typeof window === 'undefined'
    ? getStoredAcquisitionContext()
    : resolveAcquisitionFromUrl(window.location.href);
  const utmId = acquisition?.utm_id || undefined;
  // A matching cookie preserves the click's original creation time. With no
  // captured touch it remains the available legacy evidence; a new unrelated
  // campaign must not inherit it.
  const cookieClick = fbclidFromFbc(identifiers.fbc);
  const fbc = cookieClick && (!acquisition || cookieClick === acquisition.fbclid)
    ? identifiers.fbc
    : acquisition?.fbc || undefined;

  return {
    sourceUrl: sanitizeTrackingSourceUrl(acquisition?.source_url ?? getTrackingSourceUrl()),
    pageUrl: getCurrentPageTrackingUrl(),
    ...identifiers,
    // Browser identifiers stay stable; ad click identifiers describe this
    // coherent touch rather than a different campaign stored earlier.
    fbc,
    gclid: acquisition?.gclid || undefined,
    gbraid: acquisition?.gbraid || undefined,
    wbraid: acquisition?.wbraid || undefined,
    ttclid: acquisition?.ttclid || undefined,
    fbclid: acquisition?.fbclid || undefined,
    metaCampaignId: acquisition?.meta_campaign_id || undefined,
    metaAdsetId: acquisition?.meta_adset_id || undefined,
    metaAdId: acquisition?.meta_ad_id || undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    utmParams: utmId ? { ...(utmParams ?? {}), utm_id: utmId } : utmParams,
    acquisition: acquisition
      ? {
          network: acquisition.network || undefined,
          source: acquisition.utm_source || acquisition.network || undefined,
          medium: acquisition.utm_medium || undefined,
          campaign: acquisition.utm_campaign || acquisition.campaign || undefined,
          adGroup: acquisition.ad_group || undefined,
          ad: acquisition.ad || undefined,
          creative: acquisition.creative || acquisition.utm_content || undefined,
          keyword: acquisition.keyword || acquisition.utm_term || undefined,
        }
      : undefined,
    ...getAdConsentRequestFields(),
  };
}

/**
 * Track event to Google Analytics 4
 */
function trackToGA4(
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string,
  attempt = 0,
  recoveredPagePath?: string
): void {
  if (typeof window === 'undefined') return;
  if (recoveredPagePath &&
    normalizedGa4PagePath(window.location.pathname) !== normalizedGa4PagePath(recoveredPagePath)) return;
  if (isInternalAnalyticsBuild() || isInternalAnalyticsTestUser()) {
    pendingGa4PageView = null;
    return;
  }
  if (!hasTrackingConsent()) {
    if (!isNativeRuntime() && getTrackingConsentStatus() === 'pending' &&
      eventName === 'page_view' && typeof params?.page_path === 'string') {
      pendingGa4PageView = { params: { ...params }, eventId };
    } else if (getTrackingConsentStatus() === 'denied') {
      clearPendingGa4Tracking();
    }
    return;
  }
  if (eventName === 'page_view') pendingGa4PageView = null;

  // A weak password is expected validation. Auth also emits a field-level
  // diagnostic for the same attempt; keep that detail in the warehouse only.
  const isWeakPasswordValidation = params?.reason === 'weak_password';
  if (eventName === 'signup_field_error' && isWeakPasswordValidation) return;

  const ga4EventMap: Record<string, string> = {
    lead: 'generate_lead',
    checkout_started: 'begin_checkout',
    initiate_checkout: 'begin_checkout',
    trial_started: 'start_trial',
  };

  const ga4SafeParams: Record<string, unknown> = { ...(params || {}) };
  for (const [reservedName, contextName] of Object.entries(GA4_EVENT_CONTEXT_PARAM_ALIASES)) {
    if (ga4SafeParams[reservedName] !== undefined && ga4SafeParams[contextName] === undefined) {
      ga4SafeParams[contextName] = ga4SafeParams[reservedName];
    }
    delete ga4SafeParams[reservedName];
  }

  const mappedEventName = eventName === 'signup_error' && isWeakPasswordValidation
    ? 'signup_validation'
    : ga4EventMap[eventName] || eventName;

  if (isNativeRuntime()) {
    const epoch = ga4TrackingEpoch;
    const canDeliver = () => epoch === ga4TrackingEpoch && hasTrackingConsent() &&
      !isInternalAnalyticsBuild() && !isInternalAnalyticsTestUser();
    void loadInstallEnvironment().then(() => {
      if (!canDeliver()) return;
      return trackNativeFirebaseAnalyticsEvent(mappedEventName, ga4SafeParams, eventId, canDeliver);
    });
    return;
  }

  if (!window.gtag) {
    if (attempt >= GA4_LOAD_RETRY_LIMIT) return;
    const timer = window.setTimeout(() => {
      ga4RetryTimers.delete(timer);
      trackToGA4(eventName, params, eventId, attempt + 1, recoveredPagePath);
    }, GA4_LOAD_RETRY_MS);
    ga4RetryTimers.add(timer);
    return;
  }

  const eventParams = {
    ...getGa4AttributionParams(),
    ...ga4SafeParams,
    ...(eventId ? { event_id: eventId } : {}),
    ...(window.__JP_GA4_MEASUREMENT_IDS__?.length
      ? { send_to: window.__JP_GA4_MEASUREMENT_IDS__ }
      : {}),
  };

  window.gtag('event', mappedEventName, eventParams);
}

/**
 * Track event to Meta Pixel.
 *
 * STRICT ALLOW LIST: only events mapped below are forwarded to Meta. Every
 * other call is a no-op. Meta's campaign optimization and audience tooling
 * only understand their ~20 standard events, and the Events Manager list
 * is append-only (deleted events stay visible forever) so forwarding UX
 * telemetry like `paywall_impression`, `signup_error`, `onboarding_step_viewed`,
 * etc. just pollutes the dashboard and the custom-conversion builder.
 *
 * If you want to add a new Meta event: add it to this map AND to the
 * eventNameMap in `supabase/functions/meta-conversions-api/index.ts` — the
 * two must stay in sync so pixel + CAPI dedupe on the same event_id.
 *
 * Warehouse / GA4 / PostHog analytics are unaffected — every other
 * `trackEvent()` call still flows through `trackToGA4` and `trackToServer`.
 */
const META_STANDARD_EVENT_MAP: Record<string, string> = {
  page_view: 'PageView',
  lead: 'Lead',
  sign_up: 'CompleteRegistration',
  view_content: 'ViewContent',
  checkout_started: 'InitiateCheckout',
  initiate_checkout: 'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  trial_started: 'StartTrial',
  start_trial: 'StartTrial',
  subscription_purchase: 'Subscribe',
  purchase: 'Purchase',
};

function buildMetaParams(params?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!params) return params;

  const metaParams = { ...params };
  const rawItems = Array.isArray(metaParams.items) ? metaParams.items : [];
  const firstItem =
    rawItems.length > 0 && rawItems[0] && typeof rawItems[0] === 'object'
      ? rawItems[0] as Record<string, unknown>
      : null;
  const itemId =
    typeof firstItem?.item_id === 'string'
      ? firstItem.item_id
      : typeof metaParams.content_id === 'string'
        ? metaParams.content_id
        : null;
  const itemName =
    typeof firstItem?.item_name === 'string'
      ? firstItem.item_name
      : typeof metaParams.content_title === 'string'
        ? metaParams.content_title
        : typeof metaParams.content_name === 'string'
          ? metaParams.content_name
          : null;
  const itemPrice =
    typeof firstItem?.price === 'number'
      ? firstItem.price
      : typeof metaParams.value === 'number'
        ? metaParams.value
        : null;

  if (itemId && !Array.isArray(metaParams.content_ids)) {
    metaParams.content_ids = [itemId];
  }
  if (itemName && typeof metaParams.content_name !== 'string') {
    metaParams.content_name = itemName;
  }
  if (itemId && !Array.isArray(metaParams.contents)) {
    metaParams.contents = [
      {
        id: itemId,
        quantity: 1,
        ...(typeof itemPrice === 'number' ? { item_price: itemPrice } : {}),
      },
    ];
  }

  return metaParams;
}

function trackToMeta(
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string,
  userEmail?: string,
  userId?: string,
  attempt = 0
): void {
  // Native conversions reach Meta through AppsFlyer and RevenueCat; a
  // browser pixel send from the app would count the member twice.
  if (isNativeRuntime()) return;
  if (typeof window === 'undefined' || !hasTrackingConsent()) return;

  const metaEvent = META_STANDARD_EVENT_MAP[eventName];
  if (!metaEvent) return;

  const metaPixelEventKey = eventId ? getMetaPixelEventStateKey(metaEvent, eventId) : null;
  const metaPixelEventState = metaPixelEventKey ? readMetaPixelEventState(metaPixelEventKey) : null;
  if (metaPixelEventState === 'sent') return;
  if (metaPixelEventState === 'pending' && attempt === 0) return;
  if (metaPixelEventKey && !metaPixelEventState && attempt === 0) {
    writeMetaPixelEventState(metaPixelEventKey, 'pending');
  }

  if (!window.fbq) {
    if (attempt >= META_LOAD_RETRY_LIMIT) return;
    window.setTimeout(() => {
      trackToMeta(eventName, params, eventId, userEmail, userId, attempt + 1);
    }, META_LOAD_RETRY_MS);
    return;
  }

  const effectiveUserEmail = getEffectiveTrackingUserEmail(userEmail);
  if (isInternalAnalyticsTestEmail(effectiveUserEmail)) return;

  setMetaAdvancedMatchingData({
    em: effectiveUserEmail,
    external_id: normalizeMetaExternalId(userId) ?? getStoredTrackingUserId(),
  });
  const metaParams = buildMetaParams(params);

  if (eventId) {
    window.fbq('track', metaEvent, metaParams, { eventID: eventId });
    if (metaPixelEventKey) writeMetaPixelEventState(metaPixelEventKey, 'sent');
    return;
  }

  window.fbq('track', metaEvent, metaParams);
}

type MetaPixelEventState = 'pending' | 'sent';

type ClientConversionDedupeState = {
  trackedAt: number;
};

function getMetaPixelEventStateKey(metaEvent: string, eventId: string): string {
  return `${META_PIXEL_EVENT_STATE_PREFIX}${metaEvent}:${eventId}`;
}

function readMetaPixelEventState(key: string): MetaPixelEventState | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const state = parsed?.state;
    const createdAt = Number(parsed?.createdAt || 0);
    if (state !== 'pending' && state !== 'sent') return null;
    if (
      state === 'pending' &&
      Number.isFinite(createdAt) &&
      createdAt > 0 &&
      Date.now() - createdAt > META_PIXEL_PENDING_TTL_MS
    ) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

function writeMetaPixelEventState(key: string, state: MetaPixelEventState): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(
      key,
      JSON.stringify({
        state,
        createdAt: Date.now(),
      })
    );
  } catch {
    // Ignore storage failures; the page-level guards still cover normal flows.
  }
}

function readClientConversionDedupeState(key: string): ClientConversionDedupeState | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    if (raw === '1') {
      return { trackedAt: Date.now() };
    }

    const parsed = JSON.parse(raw);
    const trackedAt = Number(parsed?.trackedAt || 0);
    if (!Number.isFinite(trackedAt) || trackedAt <= 0) {
      window.localStorage.removeItem(key);
      return null;
    }
    if (Date.now() - trackedAt > CLIENT_CONVERSION_DEDUPE_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }

    return { trackedAt };
  } catch {
    return null;
  }
}

function writeClientConversionDedupeState(key: string): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        trackedAt: Date.now(),
      } satisfies ClientConversionDedupeState)
    );
  } catch {
    // Ignore storage failures; sessionStorage and vendor event IDs still help.
  }
}

/** True when this browser already marked the conversion id. Does not write. */
export function isClientConversionTracked(eventId: string): boolean {
  if (typeof window === 'undefined' || !eventId) return false;

  const key = `${CLIENT_CONVERSION_DEDUPE_PREFIX}${eventId}`;
  const sessionKey = `${key}:session`;

  try {
    if (window.sessionStorage.getItem(sessionKey) === '1') return true;
  } catch {
    // Ignore storage failures.
  }

  return Boolean(readClientConversionDedupeState(key));
}

/**
 * Mark a browser-owned conversion event as tracked across tabs and revisits.
 * These events can be sent directly to ad platforms without creating a fresh
 * `conversion_events` row, so tab-scoped dedupe is not enough for success URLs.
 */
export function markClientConversionTracked(eventId: string): boolean {
  if (typeof window === 'undefined') return true;
  if (!eventId) return false;

  const key = `${CLIENT_CONVERSION_DEDUPE_PREFIX}${eventId}`;
  const sessionKey = `${key}:session`;

  try {
    if (window.sessionStorage.getItem(sessionKey) === '1') return false;
  } catch {
    // Ignore storage failures.
  }

  if (readClientConversionDedupeState(key)) {
    try {
      window.sessionStorage.setItem(sessionKey, '1');
    } catch {
      // Ignore storage failures.
    }
    return false;
  }

  writeClientConversionDedupeState(key);
  try {
    window.sessionStorage.setItem(sessionKey, '1');
  } catch {
    // Ignore storage failures; vendor event IDs still help.
  }

  return true;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function waitForMetaPixelEventSent(eventName: string, eventId: string): Promise<void> {
  if (typeof window === 'undefined' || !eventId) return;

  const metaEvent = META_STANDARD_EVENT_MAP[eventName];
  if (!metaEvent) return;

  const stateKey = getMetaPixelEventStateKey(metaEvent, eventId);
  const initialState = readMetaPixelEventState(stateKey);
  if (initialState === 'sent') return;
  if (initialState !== 'pending') return;

  const startedAt = Date.now();
  while (Date.now() - startedAt < META_CAPI_PIXEL_WAIT_MS) {
    await wait(META_CAPI_PIXEL_WAIT_POLL_MS);
    const state = readMetaPixelEventState(stateKey);
    if (state === 'sent') return;
    if (state !== 'pending') return;
  }
}

function normalizeMetaAdvancedMatchingEmail(email?: string): string | undefined {
  if (!email) return undefined;
  const normalized = email.trim().toLowerCase();
  return normalized.includes('@') ? normalized : undefined;
}

function normalizeMetaExternalId(userId?: string): string | undefined {
  const normalized = userId?.trim();
  return normalized ? normalized : undefined;
}

function getStoredTrackingUserId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    return normalizeMetaExternalId(window.localStorage.getItem('tracking_user_id') ?? undefined);
  } catch {
    return undefined;
  }
}

function getStoredTrackingUserEmail(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    return normalizeMetaAdvancedMatchingEmail(
      window.localStorage.getItem('tracking_user_email') ?? undefined
    );
  } catch {
    return undefined;
  }
}

function getEffectiveTrackingUserEmail(userEmail?: string): string | undefined {
  return normalizeMetaAdvancedMatchingEmail(userEmail) ?? getStoredTrackingUserEmail();
}

function storeTrackingUserEmail(userEmail?: string): void {
  const normalizedEmail = normalizeMetaAdvancedMatchingEmail(userEmail);
  if (!normalizedEmail || typeof window === 'undefined' || !hasTrackingConsent()) return;

  try {
    window.localStorage.setItem('tracking_user_email', normalizedEmail);
  } catch {
    // Ignore storage failures.
  }
}

function normalizeTrackingName(value?: string | null): string | undefined {
  const normalized = value?.trim().replace(/\s+/g, ' ');
  return normalized ? normalized : undefined;
}

export function splitTrackingFullName(fullName?: string | null): {
  userFirstName?: string;
  userLastName?: string;
} {
  const normalized = normalizeTrackingName(fullName);
  if (!normalized) return {};

  const parts = normalized.split(' ').filter(Boolean);
  return {
    userFirstName: parts[0],
    ...(parts.length > 1 ? { userLastName: parts.slice(1).join(' ') } : {}),
  };
}

function setMetaAdvancedMatchingData(data: { em?: string; external_id?: string }): void {
  if (typeof window === 'undefined' || !hasTrackingConsent()) return;

  const next = {
    ...(window.__JP_META_USER_DATA__ ?? {}),
    ...(data.em ? { em: data.em } : {}),
    ...(data.external_id ? { external_id: data.external_id } : {}),
  };

  if (!next.em && !next.external_id) return;

  window.__JP_META_USER_DATA__ = next;
  if (window.fbq) {
    window.fbq('set', 'userData', next);
    syncMetaManualAdvancedMatchingInit(next);
  }
}

function setTikTokAdvancedMatchingData(data: { email?: string; external_id?: string }): void {
  if (typeof window === 'undefined' || !hasTrackingConsent()) return;

  const next = {
    ...(window.__JP_TIKTOK_USER_DATA__ ?? {}),
    ...(data.email ? { email: data.email } : {}),
    ...(data.external_id ? { external_id: data.external_id } : {}),
  };

  if (!next.email && !next.external_id) return;

  window.__JP_TIKTOK_USER_DATA__ = next;
  window.ttq?.identify?.(next);
}

/**
 * Hands an identity the ad data policy allowed to the SDKs already running
 * (no script reinjection): Meta advanced matching (fbq set userData, and init
 * with the data for each configured pixel), TikTok identify, and the AppsFlyer
 * Web customer user id. No-op without ad consent or without identity.
 */
export function applyLiveAdIdentity(identity: {
  email: string | null;
  externalId: string | null;
  appsFlyerCustomerUserId: string | null;
}): void {
  if (typeof window === 'undefined' || !hasTrackingConsent()) return;
  const email = normalizeMetaAdvancedMatchingEmail(identity.email ?? undefined);
  const externalId = normalizeMetaExternalId(identity.externalId ?? undefined);
  if (email || externalId) {
    setMetaAdvancedMatchingData({ em: email, external_id: externalId });
    setTikTokAdvancedMatchingData({ email, external_id: externalId });
  }
  const customerUserId = identity.appsFlyerCustomerUserId?.trim();
  if (customerUserId) {
    try {
      window.AF?.('pba', 'setCustomerUserId', customerUserId);
    } catch {
      // Web SDK not ready; the next injection carries the id.
    }
  }
}

function getConfiguredMetaPixelIds(): string[] {
  if (typeof window === 'undefined' || !Array.isArray(window.__JP_META_PIXEL_IDS__)) {
    return [];
  }

  return Array.from(new Set(
    window.__JP_META_PIXEL_IDS__
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter((id) => /^\d{6,}$/.test(id))
  ));
}

function syncMetaManualAdvancedMatchingInit(data: { em?: string; external_id?: string }): void {
  if (typeof window === 'undefined' || !window.fbq) return;

  const pixelIds = getConfiguredMetaPixelIds();
  if (pixelIds.length === 0) return;

  const initKey = JSON.stringify({
    em: data.em || '',
    external_id: data.external_id || '',
  });
  const initKeys = window.__JP_META_ADVANCED_MATCHING_INIT_KEYS__ || {};
  window.__JP_META_ADVANCED_MATCHING_INIT_KEYS__ = initKeys;

  pixelIds.forEach((pixelId) => {
    if (initKeys[pixelId] === initKey) return;
    window.fbq?.('init', pixelId, data);
    initKeys[pixelId] = initKey;
  });
}

/**
 * The Journey funnel sale's own label, when that action is configured and
 * the event carries a journey_* offer_id (the Challenge never qualifies).
 */
function getGoogleAdsKindLabel(
  config: NonNullable<Window['__JP_GOOGLE_ADS_CONFIGS__']>[number],
  eventName: string,
  kind: GoogleAdsConversionKind | undefined,
  offerId: unknown
): string | null {
  if (!kind || !isJourneyFunnelOfferId(offerId)) return null;
  if (kind === 'journey_purchase' && eventName === 'purchase') {
    return config.journeyPurchaseConversionLabel || null;
  }
  if (kind === 'journey_upsell' && eventName === 'subscription_purchase') {
    return config.journeyUpsellConversionLabel || null;
  }
  if (kind === 'journey_trial' && (eventName === 'trial_started' || eventName === 'start_trial')) {
    return config.journeyTrialConversionLabel || null;
  }
  return null;
}

function getGoogleAdsSendTo(
  config: NonNullable<Window['__JP_GOOGLE_ADS_CONFIGS__']>[number],
  eventName: string,
  kind?: GoogleAdsConversionKind,
  offerId?: unknown
): string | null {
  const defaultLabel =
    // Both funnel email captures ('lead') and account creations ('sign_up')
    // feed the Google Ads "Lead" action. The action counts One per click, so
    // an email capture followed by signup from the same ad click stays 1.
    eventName === 'lead' || eventName === 'sign_up'
      ? config.leadConversionLabel || config.conversionLabel
      : eventName === 'checkout_started' || eventName === 'initiate_checkout'
        ? config.checkoutConversionLabel
      : eventName === 'trial_started' || eventName === 'start_trial'
        ? config.trialConversionLabel
      : eventName === 'purchase'
          ? config.purchaseConversionLabel
      : eventName === 'subscription_purchase'
          ? config.subscriptionPurchaseConversionLabel || config.purchaseConversionLabel
          : null;
  const label = getGoogleAdsKindLabel(config, eventName, kind, offerId) || defaultLabel;

  if (!label) return null;
  return `${config.conversionId}/${label}`;
}

function normalizeEmailForEnhancedConversions(email?: string): string | undefined {
  if (!email) return undefined;
  const trimmed = email.trim().toLowerCase();
  if (!trimmed.includes('@')) return undefined;
  return trimmed;
}

// Same rule as supabase/functions/_shared/internalAnalytics.ts.
function isInternalAnalyticsTestEmail(email?: string): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  return (
    normalized.includes('alexgallner') ||
    normalized.includes('journalparty.com') ||
    (normalized.startsWith('alex+') && normalized.endsWith('@gmail.com'))
  );
}

/**
 * Internal test user check used by trackEvent's platform skip. Falls back to
 * the stored tracking email when no email is passed.
 */
export function isInternalAnalyticsTestUser(email?: string): boolean {
  return isInternalAnalyticsTestEmail(getEffectiveTrackingUserEmail(email));
}

// True while this page has a buyer's enhanced conversions data set on the
// Google tag (gtag('set', 'user_data') persists for every later hit).
let googleAdsUserDataOnTag = false;

/**
 * Removes the buyer's enhanced conversions data from the Google tag.
 * gtag('set') merges objects into what is stored, so {} or a smaller object
 * keeps the earlier buyer's fields; null removes user_data completely
 * (checked on gtag.js, Oct 19 2026).
 */
function clearGoogleAdsUserData(): void {
  if (!googleAdsUserDataOnTag || typeof window === 'undefined') return;
  googleAdsUserDataOnTag = false;
  try {
    window.gtag?.('set', 'user_data', null);
  } catch {
    // gtag not ready: nothing was kept on it.
  }
}

/** What a Google Ads conversion carries besides the event params. */
type GoogleAdsConversionExtras = {
  kind?: GoogleAdsConversionKind;
  /** Known buyer details other than the email (enhanced conversions). */
  identity?: Omit<GoogleAdsUserIdentity, 'email'>;
};

function trackToGoogleAds(
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string,
  userEmail?: string,
  extras: GoogleAdsConversionExtras = {},
  attempt = 0
): void {
  // Native reports to Google Ads through AppsFlyer only (no Firebase import).
  if (isNativeRuntime()) return;
  if (typeof window === 'undefined' || !hasTrackingConsent()) return;

  if (!window.gtag) {
    queuePendingGoogleAdsConversion(eventName, params, eventId, userEmail, extras);
    if (attempt >= GOOGLE_ADS_LOAD_RETRY_LIMIT) return;
    window.setTimeout(() => {
      trackToGoogleAds(eventName, params, eventId, userEmail, extras, attempt + 1);
    }, GOOGLE_ADS_LOAD_RETRY_MS);
    return;
  }

  const configs = Array.isArray(window.__JP_GOOGLE_ADS_CONFIGS__)
    ? window.__JP_GOOGLE_ADS_CONFIGS__.filter(Boolean)
    : [];
  if (configs.length === 0) {
    queuePendingGoogleAdsConversion(eventName, params, eventId, userEmail, extras);
    if (attempt >= GOOGLE_ADS_LOAD_RETRY_LIMIT) return;
    window.setTimeout(() => {
      trackToGoogleAds(eventName, params, eventId, userEmail, extras, attempt + 1);
    }, GOOGLE_ADS_LOAD_RETRY_MS);
    return;
  }

  const normalizedEmail =
    normalizeEmailForEnhancedConversions(userEmail) ?? getStoredTrackingUserEmail();
  // Enhanced Conversions: plain values; gtag.js normalizes and hashes them
  // before sending. Set on the tag before the conversion fires (Google's
  // documented gtag('set', 'user_data') setup) and repeated on the event.
  // Every conversion first clears what an earlier conversion on this page
  // set ('set' merges), so it never carries another buyer's fields.
  const userData = buildGoogleAdsUserData({ ...(extras.identity ?? {}), email: normalizedEmail });
  const sendTos = configs
    .map((config) => getGoogleAdsSendTo(config, eventName, extras.kind, params?.offer_id))
    .filter((sendTo): sendTo is string => Boolean(sendTo));
  if (sendTos.length > 0) {
    clearGoogleAdsUserData();
    if (userData) {
      window.gtag?.('set', 'user_data', userData);
      googleAdsUserDataOnTag = true;
    }
  }

  sendTos.forEach((sendTo) => {
    const payload: Record<string, unknown> = {
      send_to: sendTo,
    };

    if (typeof params?.value === 'number') {
      payload.value = params.value;
    }
    if (typeof params?.currency === 'string') {
      payload.currency = params.currency;
    }
    if (typeof params?.transaction_id === 'string') {
      payload.transaction_id = params.transaction_id;
    }
    if (eventId) {
      payload.event_id = eventId;
    }

    if (userData) {
      payload.user_data = userData;
    }

    window.gtag?.('event', 'conversion', payload);
    removePendingGoogleAdsConversion(eventName, eventId);
  });
}

type PendingGoogleAdsConversion = {
  eventName: string;
  params?: Record<string, unknown>;
  eventId?: string;
  userEmail?: string;
  googleAdsConversion?: GoogleAdsConversionKind;
  identity?: Omit<GoogleAdsUserIdentity, 'email'>;
  queuedAt: number;
};

function isGoogleAdsConversionEvent(eventName: string): boolean {
  return [
    'lead',
    'sign_up',
    'checkout_started',
    'initiate_checkout',
    'trial_started',
    'start_trial',
    'purchase',
    'subscription_purchase',
  ].includes(eventName);
}

function getPendingGoogleAdsConversionKey(eventName: string, eventId?: string): string {
  return `${eventName}:${eventId || 'no-event-id'}`;
}

function readPendingGoogleAdsConversions(): PendingGoogleAdsConversion[] {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.sessionStorage.getItem(GOOGLE_ADS_PENDING_CONVERSIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((item): item is PendingGoogleAdsConversion =>
          Boolean(item && typeof item.eventName === 'string' && typeof item.queuedAt === 'number')
        )
      : [];
  } catch {
    return [];
  }
}

function writePendingGoogleAdsConversions(items: PendingGoogleAdsConversion[]): void {
  if (typeof window === 'undefined') return;

  try {
    if (items.length === 0) {
      window.sessionStorage.removeItem(GOOGLE_ADS_PENDING_CONVERSIONS_KEY);
      return;
    }
    window.sessionStorage.setItem(GOOGLE_ADS_PENDING_CONVERSIONS_KEY, JSON.stringify(items));
  } catch {
    // Ignore storage failures. The in-page retry still covers normal load timing.
  }
}

function queuePendingGoogleAdsConversion(
  eventName: string,
  params?: Record<string, unknown>,
  eventId?: string,
  userEmail?: string,
  extras: GoogleAdsConversionExtras = {}
): void {
  if (!isGoogleAdsConversionEvent(eventName)) return;
  if (typeof window === 'undefined') return;

  const key = getPendingGoogleAdsConversionKey(eventName, eventId);
  const pending = readPendingGoogleAdsConversions();
  const next: PendingGoogleAdsConversion = {
    eventName,
    params,
    eventId,
    userEmail,
    ...(extras.kind ? { googleAdsConversion: extras.kind } : {}),
    ...(extras.identity ? { identity: extras.identity } : {}),
    queuedAt: Date.now(),
  };
  const withoutDuplicate = pending.filter(
    (item) => getPendingGoogleAdsConversionKey(item.eventName, item.eventId) !== key
  );
  writePendingGoogleAdsConversions([...withoutDuplicate, next].slice(-20));
}

/** Drops every queued conversion (consent reset). */
function clearPendingGoogleAdsConversions(): void {
  writePendingGoogleAdsConversions([]);
}

/**
 * Keeps queued conversions but removes the buyer's email, name, phone and
 * postal code from them (sign-out, member switch, ad identity not allowed).
 */
function stripPendingGoogleAdsConversionIdentity(): void {
  const pending = readPendingGoogleAdsConversions();
  if (!pending.some((item) => item.userEmail || item.identity)) return;
  writePendingGoogleAdsConversions(
    pending.map(({ userEmail: _userEmail, identity: _identity, ...rest }) => rest)
  );
}

function removePendingGoogleAdsConversion(eventName: string, eventId?: string): void {
  const key = getPendingGoogleAdsConversionKey(eventName, eventId);
  const pending = readPendingGoogleAdsConversions();
  const next = pending.filter(
    (item) => getPendingGoogleAdsConversionKey(item.eventName, item.eventId) !== key
  );

  if (next.length !== pending.length) {
    writePendingGoogleAdsConversions(next);
  }
}

export function flushPendingGoogleAdsConversions(): void {
  if (typeof window === 'undefined' || !hasTrackingConsent()) return;
  if (!window.gtag) return;
  const configs = Array.isArray(window.__JP_GOOGLE_ADS_CONFIGS__)
    ? window.__JP_GOOGLE_ADS_CONFIGS__.filter(Boolean)
    : [];
  if (configs.length === 0) return;

  const pending = readPendingGoogleAdsConversions();
  pending.forEach((item) => {
    trackToGoogleAds(item.eventName, item.params, item.eventId, item.userEmail, {
      kind: item.googleAdsConversion,
      identity: item.identity,
    });
  });
}

/**
 * Track event to TikTok Pixel.
 *
 * STRICT ALLOW LIST: mirrors `TIKTOK_EVENT_NAME_MAP` in
 * `supabase/functions/tiktok-events-api/eventNames.ts`. The two must stay in
 * sync so Pixel + Events API dedupe on the same `event_id`
 * (src/__tests__/edge-functions/tiktokEventNames.test.ts checks this).
 *
 * Event name notes:
 * - `CompletePayment` was deprecated by TikTok and replaced with `Purchase`
 *   for both Pixel and Events API; always use `Purchase` for new tracking.
 * - Trial starts send `StartTrial` and paid subscriptions send `Subscribe`
 *   (TikTok: "Use StartTrial to measure trial initiations";
 *   ads.tiktok.com/help/article/standard-events-parameters). One-time buys
 *   (Journey, Challenge, a la carte) send `Purchase`.
 * - `Lead` is kept because TikTok still accepts it as a standard event for
 *   lead-generation campaigns.
 * - `page_view` is intentionally omitted here because the TikTok Pixel SDK
 *   auto-fires `ttq.page()` on init (see MarketingScripts.tsx). Adding it
 *   here would double-count the initial page view.
 */
export const TIKTOK_STANDARD_EVENT_MAP: Record<string, string> = {
  lead: 'Lead',
  sign_up: 'CompleteRegistration',
  view_content: 'ViewContent',
  add_to_cart: 'AddToCart',
  checkout_started: 'InitiateCheckout',
  initiate_checkout: 'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  trial_started: 'StartTrial',
  start_trial: 'StartTrial',
  subscription_purchase: 'Subscribe',
  purchase: 'Purchase',
};

function normalizeTikTokString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeTikTokProductId(value: unknown): string | undefined {
  const normalized = normalizeTikTokString(value);
  if (!normalized) return undefined;
  return normalized
    .toLowerCase()
    .replace(/[^a-z0-9_:-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100) || undefined;
}

function firstTikTokItem(params?: Record<string, unknown>):
  | {
      content_id?: unknown;
      content_ids?: unknown;
      id?: unknown;
      item_id?: unknown;
      content_name?: unknown;
      item_name?: unknown;
      content_category?: unknown;
      item_category?: unknown;
      price?: unknown;
      item_price?: unknown;
      quantity?: unknown;
    }
  | null {
  const items = Array.isArray(params?.contents)
    ? params.contents
    : Array.isArray(params?.items)
      ? params.items
      : [];
  if (items.length === 0) return null;
  const [first] = items;
  if (!first || typeof first !== 'object') return null;
  return first as {
    content_id?: unknown;
    content_ids?: unknown;
    id?: unknown;
    item_id?: unknown;
    content_name?: unknown;
    item_name?: unknown;
    content_category?: unknown;
    item_category?: unknown;
    price?: unknown;
    item_price?: unknown;
    quantity?: unknown;
  };
}

function getFirstContentId(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    return value.map(normalizeTikTokString).find(Boolean);
  }

  return normalizeTikTokString(value);
}

function inferTikTokContentId(
  params: Record<string, unknown>,
  firstItem: ReturnType<typeof firstTikTokItem>
): string | undefined {
  const explicit =
    normalizeTikTokString(params.content_id) ||
    getFirstContentId(params.content_ids) ||
    normalizeTikTokString(params.item_id) ||
    normalizeTikTokString(firstItem?.content_id) ||
    getFirstContentId(firstItem?.content_ids) ||
    normalizeTikTokString(firstItem?.item_id) ||
    normalizeTikTokString(firstItem?.id);
  if (explicit) return explicit;

  const tier = normalizeTikTokProductId(params.tier);
  if (tier) return `journal_party_premium_${tier}`;

  const paywallFeature = normalizeTikTokProductId(params.paywall_feature);
  if (paywallFeature) return `journal_party_${paywallFeature}`;

  const contentName =
    normalizeTikTokProductId(params.content_name) ||
    normalizeTikTokProductId(params.content_title) ||
    normalizeTikTokProductId(firstItem?.content_name) ||
    normalizeTikTokProductId(firstItem?.item_name);
  return contentName ? `journal_party_${contentName}` : undefined;
}

function buildTikTokContent(
  item: Record<string, unknown>,
  fallbackContentType: 'product' | 'product_group'
): Record<string, unknown> | null {
  const itemId =
    normalizeTikTokString(item.content_id) ||
    getFirstContentId(item.content_ids) ||
    normalizeTikTokString(item.item_id) ||
    normalizeTikTokString(item.id);
  if (!itemId) return null;

  const content: Record<string, unknown> = {
    content_id: itemId,
    content_type: item.content_type === 'product_group' ? 'product_group' : fallbackContentType,
  };
  const itemName = normalizeTikTokString(item.content_name) || normalizeTikTokString(item.item_name);
  const itemCategory =
    normalizeTikTokString(item.content_category) || normalizeTikTokString(item.item_category);
  if (itemName) content.content_name = itemName;
  if (itemCategory) content.content_category = itemCategory;
  if (typeof item.price === 'number') content.price = item.price;
  if (typeof item.item_price === 'number' && content.price == null) content.price = item.item_price;
  if (typeof item.quantity === 'number') content.quantity = item.quantity;

  return content;
}

function buildTikTokParams(
  eventName: string,
  params?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!params) return params;

  const commerceEvents = new Set([
    'view_content',
    'add_to_cart',
    'checkout_started',
    'initiate_checkout',
    'add_payment_info',
    'purchase',
    'subscription_purchase',
  ]);
  if (!commerceEvents.has(eventName) && !params.content_id && !params.content_ids && !params.items) {
    return params;
  }

  const firstItem = firstTikTokItem(params);
  const itemId = inferTikTokContentId(params, firstItem);
  const itemName =
    normalizeTikTokString(params.content_name) ||
    normalizeTikTokString(params.content_title) ||
    normalizeTikTokString(firstItem?.content_name) ||
    normalizeTikTokString(firstItem?.item_name);
  const itemCategory =
    normalizeTikTokString(params.content_category) ||
    normalizeTikTokString(firstItem?.content_category) ||
    normalizeTikTokString(firstItem?.item_category);
  const contentType = typeof params.content_type === 'string' ? params.content_type : '';
  const normalizedContentType = contentType === 'product_group' ? 'product_group' : 'product';

  const tiktokParams = { ...params };
  if (!tiktokParams.content_id && itemId) tiktokParams.content_id = itemId;
  if (!tiktokParams.content_ids && itemId) tiktokParams.content_ids = [itemId];
  if (!tiktokParams.content_name && itemName) tiktokParams.content_name = itemName;
  if (!tiktokParams.content_category && itemCategory) tiktokParams.content_category = itemCategory;
  if (contentType !== 'product' && contentType !== 'product_group') {
    tiktokParams.content_type = normalizedContentType;
  }

  const sourceContents = Array.isArray(params.contents)
    ? params.contents
    : Array.isArray(params.items)
      ? params.items
      : [];
  const contents = sourceContents
    .filter((item): item is Record<string, unknown> => Boolean(item && typeof item === 'object'))
    .map((item) => buildTikTokContent(item, normalizedContentType))
    .filter((item): item is Record<string, unknown> => Boolean(item));

  if (!tiktokParams.contents && contents.length > 0) {
    tiktokParams.contents = contents;
  } else if (!tiktokParams.contents && itemId) {
    tiktokParams.contents = [
      {
        content_id: itemId,
        content_type: normalizedContentType,
        ...(itemName ? { content_name: itemName } : {}),
        ...(itemCategory ? { content_category: itemCategory } : {}),
        ...(typeof params.value === 'number' ? { price: params.value } : {}),
        quantity: 1,
      },
    ];
  }

  return tiktokParams;
}

function identifyTikTokUser(userEmail?: string): void {
  setTikTokAdvancedMatchingData({
    email: normalizeMetaAdvancedMatchingEmail(userEmail) ?? getStoredTrackingUserEmail(),
    external_id: getStoredTrackingUserId(),
  });
}

function trackToTikTok(
  eventName: string,
  params?: Record<string, unknown>,
  userEmail?: string
): void {
  // Native conversions reach TikTok through AppsFlyer and RevenueCat.
  if (isNativeRuntime()) return;
  if (typeof window === 'undefined' || !window.ttq?.track || !hasTrackingConsent()) return;

  const tiktokEvent = Object.prototype.hasOwnProperty.call(TIKTOK_STANDARD_EVENT_MAP, eventName)
    ? TIKTOK_STANDARD_EVENT_MAP[eventName]
    : undefined;
  if (!tiktokEvent) return;

  identifyTikTokUser(userEmail);
  // TikTok reads the dedupe id from the third argument (its documented
  // form), not from the event properties, so it goes there and only there.
  // tiktok-events-api sends the server copy under the same id.
  const { event_id: rawEventId, ...tiktokParams } = buildTikTokParams(eventName, params) || {};
  const eventId = typeof rawEventId === 'string' && rawEventId ? rawEventId : null;
  const ttq = window.ttq;

  if (eventName === 'checkout_started' || eventName === 'initiate_checkout') {
    if (eventId) {
      ttq.track('AddToCart', tiktokParams, { event_id: `${eventId}:add_to_cart` });
    } else {
      ttq.track('AddToCart', tiktokParams);
    }
  }

  if (eventId) {
    ttq.track(tiktokEvent, tiktokParams, { event_id: eventId });
  } else {
    ttq.track(tiktokEvent, tiktokParams);
  }
}

function normalizeAppsFlyerEventValue(value: unknown): string | number | boolean | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value;
  return null;
}

function isAppsFlyerRevenueEvent(eventName: string): boolean {
  return eventName === 'purchase' || eventName === 'subscription_purchase';
}

function isAppsFlyerNativeBillingEvent(eventName: string): boolean {
  return eventName === 'purchase' ||
    eventName === 'trial_started' ||
    eventName === 'subscription_purchase';
}

function canTrackAppsFlyerEvent(
  eventName: string,
  options: { allowNativeWithoutConsent?: boolean }
): boolean {
  const consentStatus = getTrackingConsentStatus();
  if (consentStatus === 'granted') return true;

  // ATT denial removes IDFA access; it does not require dropping anonymous
  // AppsFlyer events. Sending these allowlisted, non-billing events preserves
  // Meta AEM funnel signals with the AppsFlyer ID and device context while the
  // native plugin continues to withhold ATT-gated email and advertising IDs.
  // After the 90 second ATT fallback, AppDelegate has started AppsFlyer without
  // an answer; an unanswered prompt then sends like a denial (no IDFA).
  const nativeStatus = getNativeTrackingConsentStatus();
  if (
    isNativeIosRuntime() &&
    (nativeStatus === 'denied' || (nativeStatus === 'pending' && appsFlyerAttFallbackElapsed)) &&
    !hasNativeAppTrackingOptOut()
  ) {
    if (!isAppsFlyerNativeBillingEvent(eventName)) return true;

    // Native billing remains opt-in here because RevenueCat owns normal trial
    // and subscription delivery. This escape hatch exists only for explicitly
    // designated billing paths and prevents accidental duplicate postbacks.
    return options.allowNativeWithoutConsent === true;
  }

  // Keep web consent blocking, unresolved iOS ATT, and explicit Android
  // opt-outs unchanged.
  return false;
}

type PendingAppsFlyerAttEvent = {
  eventName: string;
  appsFlyerEventName: string;
  eventValues: Record<string, string | number | boolean>;
  allowNativeWithoutConsent: boolean;
};

let pendingAppsFlyerAttEvents: PendingAppsFlyerAttEvent[] | null = null;
let pendingAppsFlyerAttFlush: Promise<void> | null = null;

/**
 * Mirrors AppDelegate's 90 second foreground fallback. The clock starts with
 * the first held event (so it never ends before the native one) and counts
 * only while the app is visible; when it runs out the held events go out the
 * same way a denial would, and later events stop queueing.
 */
const APPSFLYER_ATT_FALLBACK_MS = 90_000;
const APPSFLYER_ATT_FALLBACK_TICK_MS = 1_000;
let appsFlyerAttFallbackElapsed = false;
let appsFlyerAttFallbackVisibleMs = 0;
let appsFlyerAttFallbackTimer: ReturnType<typeof setInterval> | null = null;

function startAppsFlyerAttFallbackClock(): void {
  if (appsFlyerAttFallbackElapsed || appsFlyerAttFallbackTimer || typeof window === 'undefined') return;
  appsFlyerAttFallbackTimer = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    appsFlyerAttFallbackVisibleMs += APPSFLYER_ATT_FALLBACK_TICK_MS;
    if (appsFlyerAttFallbackVisibleMs < APPSFLYER_ATT_FALLBACK_MS) return;
    appsFlyerAttFallbackElapsed = true;
    if (appsFlyerAttFallbackTimer) clearInterval(appsFlyerAttFallbackTimer);
    appsFlyerAttFallbackTimer = null;
    void flushPendingAppsFlyerAttEvents();
  }, APPSFLYER_ATT_FALLBACK_TICK_MS);
}

/** Test seam: drop the in-memory queue so the next read restores from sessionStorage, as after a reload. */
export function forgetPendingAppsFlyerAttEventsCacheForTests(): void {
  pendingAppsFlyerAttEvents = null;
}

/** Test seam: reset the fallback clock between tests. */
export function resetAppsFlyerAttFallbackForTests(): void {
  if (appsFlyerAttFallbackTimer) clearInterval(appsFlyerAttFallbackTimer);
  appsFlyerAttFallbackTimer = null;
  appsFlyerAttFallbackVisibleMs = 0;
  appsFlyerAttFallbackElapsed = false;
}

/**
 * iOS holds AppsFlyer start() until the member answers the ATT prompt (or 90s
 * of foreground time pass), and onboarding asks only after "How it works".
 * Events from those first screens wait here instead of being dropped, then
 * go out in order once the answer is known.
 */
function isAppsFlyerAttDecisionPending(): boolean {
  return (
    !appsFlyerAttFallbackElapsed &&
    isNativeIosRuntime() &&
    !hasNativeAppTrackingOptOut() &&
    getNativeTrackingConsentStatus() === 'pending'
  );
}

function isPendingAppsFlyerAttEvent(value: unknown): value is PendingAppsFlyerAttEvent {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.eventName === 'string' &&
    typeof item.appsFlyerEventName === 'string' &&
    Boolean(item.eventValues) &&
    typeof item.eventValues === 'object' &&
    typeof item.allowNativeWithoutConsent === 'boolean'
  );
}

function readPendingAppsFlyerAttEvents(): PendingAppsFlyerAttEvent[] {
  if (pendingAppsFlyerAttEvents) return pendingAppsFlyerAttEvents;

  let restored: PendingAppsFlyerAttEvent[] = [];
  if (typeof window !== 'undefined') {
    try {
      const raw = window.sessionStorage.getItem(APPSFLYER_ATT_PENDING_EVENTS_KEY);
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      if (Array.isArray(parsed)) {
        restored = parsed.filter(isPendingAppsFlyerAttEvent).slice(0, APPSFLYER_ATT_PENDING_EVENTS_MAX);
      }
    } catch {
      // Memory alone still holds events queued in this page load.
    }
  }

  pendingAppsFlyerAttEvents = restored;
  return restored;
}

function writePendingAppsFlyerAttEvents(items: PendingAppsFlyerAttEvent[]): void {
  pendingAppsFlyerAttEvents = items;
  if (typeof window === 'undefined') return;

  try {
    if (items.length > 0) {
      window.sessionStorage.setItem(APPSFLYER_ATT_PENDING_EVENTS_KEY, JSON.stringify(items));
    } else {
      window.sessionStorage.removeItem(APPSFLYER_ATT_PENDING_EVENTS_KEY);
    }
  } catch {
    // Ignore storage failures; the in-memory queue still works.
  }
}

function clearPendingAppsFlyerAttEvents(): void {
  writePendingAppsFlyerAttEvents([]);
}

function queuePendingAppsFlyerAttEvent(item: PendingAppsFlyerAttEvent): void {
  startAppsFlyerAttFallbackClock();
  const items = readPendingAppsFlyerAttEvents();
  // Keep the earliest events: the first funnel steps (registration, onboarding)
  // are the ones ad partners optimize on.
  if (items.length >= APPSFLYER_ATT_PENDING_EVENTS_MAX) return;
  writePendingAppsFlyerAttEvents([...items, item]);
}

async function sendNativeAppsFlyerEvent(
  appsFlyerEventName: string,
  eventValues: Record<string, string | number | boolean>
): Promise<void> {
  try {
    await logNativeAppsFlyerEvent(appsFlyerEventName, eventValues);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('AppsFlyer native tracking failed:', error);
    }
  }
}

/**
 * Sends (or drops) the events held while iOS ATT was undecided. Each one gets
 * the same gate a live event would get now: all of them after a grant, the
 * anonymous non-billing ones after a denial, none after an app opt-out.
 */
export function flushPendingAppsFlyerAttEvents(): Promise<void> {
  if (pendingAppsFlyerAttFlush) return pendingAppsFlyerAttFlush;
  if (isAppsFlyerAttDecisionPending()) {
    // A queue restored after a WebView reload still needs its fallback clock.
    if (readPendingAppsFlyerAttEvents().length > 0) startAppsFlyerAttFallbackClock();
    return Promise.resolve();
  }

  const items = readPendingAppsFlyerAttEvents();
  if (items.length === 0) return Promise.resolve();
  clearPendingAppsFlyerAttEvents();

  pendingAppsFlyerAttFlush = (async () => {
    // AppDelegate starts AppsFlyer on main right after the ATT answer (or on
    // the next didBecomeActive); give it a moment so held events land after
    // start() rather than racing it.
    if (isNativeIosRuntime()) {
      await new Promise((resolve) => setTimeout(resolve, APPSFLYER_ATT_FLUSH_DELAY_MS));
    }
    for (const item of items) {
      if (!canTrackAppsFlyerEvent(item.eventName, {
        allowNativeWithoutConsent: item.allowNativeWithoutConsent,
      })) {
        continue;
      }
      await sendNativeAppsFlyerEvent(item.appsFlyerEventName, item.eventValues);
    }
  })().finally(() => {
    pendingAppsFlyerAttFlush = null;
  });

  return pendingAppsFlyerAttFlush;
}

function buildAppsFlyerEventValues(
  eventName: string,
  params: Record<string, unknown> | undefined,
  eventId: string
): Record<string, string | number | boolean> {
  const values: Record<string, string | number | boolean> = {
    event_id: eventId,
  };

  for (const [key, value] of Object.entries(params || {})) {
    const normalized = normalizeAppsFlyerEventValue(value);
    if (normalized !== null) {
      values[key === 'event_id' ? 'source_event_id' : key] = normalized;
    }
  }

  const contentId =
    normalizeAppsFlyerEventValue(params?.content_id) ||
    (Array.isArray(params?.items) && params.items[0] && typeof params.items[0] === 'object'
      ? normalizeAppsFlyerEventValue((params.items[0] as Record<string, unknown>).item_id)
      : null) ||
    normalizeAppsFlyerEventValue(params?.program_id) ||
    normalizeAppsFlyerEventValue(params?.event_id);
  const contentName =
    normalizeAppsFlyerEventValue(params?.content_title) ||
    normalizeAppsFlyerEventValue(params?.content_name) ||
    (Array.isArray(params?.items) && params.items[0] && typeof params.items[0] === 'object'
      ? normalizeAppsFlyerEventValue((params.items[0] as Record<string, unknown>).item_name)
      : null) ||
    normalizeAppsFlyerEventValue(params?.program_name) ||
    normalizeAppsFlyerEventValue(params?.event_name);

  if (contentId) values.af_content_id = contentId;
  if (contentName) values.af_content = contentName;

  const rawValue = normalizeAppsFlyerEventValue(params?.value);
  const currency = normalizeAppsFlyerEventValue(params?.currency);
  if (typeof rawValue === 'number') {
    if (isAppsFlyerRevenueEvent(eventName)) {
      values.af_revenue = rawValue;
    } else {
      values.af_price = rawValue;
    }
  }
  if (typeof currency === 'string') values.af_currency = currency.toUpperCase();

  const orderId =
    normalizeAppsFlyerEventValue(params?.transaction_id) ||
    normalizeAppsFlyerEventValue(params?.stripe_subscription_id) ||
    normalizeAppsFlyerEventValue(params?.paypal_subscription_id);
  if (orderId) values.af_order_id = orderId;

  return values;
}

async function trackToAppsFlyer(
  eventName: string,
  params: Record<string, unknown> | undefined,
  eventId: string,
  options: { allowNativeWithoutConsent?: boolean } = {}
): Promise<void> {
  const isNative = isNativeRuntime();

  if (isNative && isAppsFlyerAttDecisionPending()) {
    const appsFlyerEventName = APPSFLYER_EVENT_MAP[eventName];
    if (!appsFlyerEventName) return;
    // RevenueCat owns billing delivery; only the explicit override may wait.
    if (isAppsFlyerNativeBillingEvent(eventName) && options.allowNativeWithoutConsent !== true) return;

    queuePendingAppsFlyerAttEvent({
      eventName,
      appsFlyerEventName,
      eventValues: buildAppsFlyerEventValues(eventName, params, eventId),
      allowNativeWithoutConsent: options.allowNativeWithoutConsent === true,
    });
    return;
  }

  // Held events go first so AppsFlyer sees the funnel in order.
  if (isNative && pendingAppsFlyerAttFlush) await pendingAppsFlyerAttFlush;

  if (!canTrackAppsFlyerEvent(eventName, options)) return;

  const appsFlyerEventName = APPSFLYER_EVENT_MAP[eventName];
  if (!appsFlyerEventName) return;

  const eventValues = buildAppsFlyerEventValues(eventName, params, eventId);

  if (!isNative) {
    if (typeof window === 'undefined' || !window.AF) return;

    const eventRevenue =
      isAppsFlyerRevenueEvent(eventName) && typeof params?.value === 'number'
        ? params.value
        : undefined;
    const eventRevenueCurrency =
      eventRevenue != null && typeof params?.currency === 'string'
        ? params.currency.toUpperCase()
        : undefined;

    window.AF('pba', 'event', {
      eventType: 'EVENT',
      eventName: appsFlyerEventName,
      ...(eventRevenue != null ? { eventRevenue } : {}),
      ...(eventRevenueCurrency ? { eventRevenueCurrency } : {}),
      eventValue: eventValues,
    });
    return;
  }

  await sendNativeAppsFlyerEvent(appsFlyerEventName, eventValues);
}

/**
 * utmParams on the track-event body only.
 * getMergedUtmParams stays raw for checkout attribution.
 * freePageAttribution keeps the utm allowlist and drops address, mailto, and over-length values.
 */
function scrubEnvelopeUtmParams(
  merged: ReturnType<typeof getMergedUtmParams>,
): ReturnType<typeof getMergedUtmParams> {
  if (!merged) return undefined;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(merged)) {
    if (value) search.set(key, value);
  }
  if ([...search.keys()].length === 0) return undefined;
  const safe = freePageAttribution(search.toString());
  const utmParams: NonNullable<ReturnType<typeof getMergedUtmParams>> = {};
  for (const key of Object.keys(merged) as Array<keyof typeof utmParams>) {
    const value = safe[key];
    if (value) utmParams[key] = value;
  }
  return Object.keys(utmParams).length > 0 ? utmParams : undefined;
}

/**
 * Track event to server-side (Supabase edge function)
 * This bypasses ad blockers and provides more reliable tracking
 */
async function trackToServer(
  eventName: string,
  params?: Record<string, unknown>,
  userId?: string,
  fanoutMode: FanoutMode = 'none',
  eventId?: string,
  userEmail?: string,
  fanoutTargets?: FanoutTarget[],
  userFirstName?: string,
  userLastName?: string,
  sourceUrl?: string,
  signupProof?: string,
  analyticsOnly?: boolean
): Promise<{ deduped: boolean } | null | 'not_sent'> {
  // Without web ad consent the server is asked only for Limited Data Use
  // conversion sends (opted out outside an opt-in jurisdiction); nothing
  // else is collected for that visitor.
  // Page views are not sent for that visitor.
  if (
    !isNativeRuntime() &&
    !hasTrackingConsent() &&
    !(fanoutMode === 'destinations' && eventName !== 'page_view' && isLimitedServerAdMode())
  ) {
    return 'not_sent';
  }

  try {
    // Native: learn the install channel (TestFlight, debug, sideload) once
    // per launch so internal builds are marked before the first event.
    if (isNativeRuntime()) await loadInstallEnvironment();

    // Get current user if not provided
    let uid = userId;
    if (!uid) {
      const { data: { user } } = await supabase.auth.getUser();
      uid = user?.id;
    }

    const identifiers = getMarketingIdentifiers();
    const eventData = buildServerEventData(params);
    await enrichNativeServerEventData(eventData);
    if (identifiers.gbraid && eventData.attr_gbraid == null) {
      eventData.attr_gbraid = identifiers.gbraid;
    }
    if (identifiers.wbraid && eventData.attr_wbraid == null) {
      eventData.attr_wbraid = identifiers.wbraid;
    }
    if (identifiers.appsflyerWebUserId && eventData.attr_appsflyer_web_user_id == null) {
      eventData.attr_appsflyer_web_user_id = identifiers.appsflyerWebUserId;
    }

    // Call the track-event edge function
    const { data, error } = await supabase.functions.invoke('track-event', {
      body: {
        eventName,
        eventId,
        eventData,
        userId: uid,
        userEmail,
        userFirstName: normalizeTrackingName(userFirstName),
        userLastName: normalizeTrackingName(userLastName),
        ...(signupProof ? { signupProof } : {}),
        // First touch for the server-side PostHog sign_up (null when this
        // device has none). Native only: track-event never forwards web
        // events to PostHog, and posthog-js records the web sign_up itself.
        ...(eventName === 'sign_up' && isNativeRuntime()
          ? { signupAttribution: buildSignupAttributionPayload() }
          : {}),
        ...(analyticsOnly ? { analyticsOnly: true } : {}),
        fanoutMode,
        fanoutTargets,
        url: sourceUrl ?? getTrackingSourceUrl(),
        ...identifiers,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        utmParams: scrubEnvelopeUtmParams(getMergedUtmParams()),
      },
    });
    // A non-2xx answer (track-event sends 503 when a sign_up was not stored)
    // arrives as `error`; success:false is treated the same: not accepted.
    if (error) return null;
    const answer = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
    if (answer?.success === false) return null;
    return { deduped: answer?.deduped === true };
  } catch (error) {
    // Silently fail - tracking should never break the app
    if (import.meta.env.DEV) {
      console.warn('Server-side tracking failed:', error);
    }
    return null;
  }
}

/**
 * Track event to PostHog. Product analytics gate (on unless opted out; ATT
 * does not apply); the PostHog module adds its own PII filtering and queues
 * calls until the lazy SDK has loaded. Returns true when the event was handed
 * to posthog-js, so track-event does not forward it a second time.
 */
function trackToPostHog(
  eventName: string,
  params: Record<string, unknown> | undefined,
  eventId: string
): boolean {
  if (typeof window === 'undefined' || !hasProductAnalyticsConsent()) return false;
  return capturePostHogEvent(eventName, { ...(params || {}), ...getAcquisitionMeasurementParams(), event_id: eventId });
}

/**
 * Main tracking function - sends to all configured platforms
 */
export async function trackEvent(
  eventName: string,
  params?: Record<string, unknown>,
  options?: TrackEventOptions
): Promise<void> {
  if (isLocalWebTrackingEnvironment()) return;

  const sanitizedParams = params ? sanitizeAnalyticsEventData(params) : undefined;
  if (
    (eventName === 'checkout_started' || eventName === 'initiate_checkout') &&
    shouldSkipDuplicateCheckoutStarted(sanitizedParams)
  ) {
    return;
  }
  const eventId = options?.eventId || createTrackingEventId();
  const effectiveUserEmail = getEffectiveTrackingUserEmail(options?.userEmail);
  const isInternalTestEvent = isInternalAnalyticsTestEmail(effectiveUserEmail);
  const sourceUrl = sanitizeTrackingSourceUrl(options?.sourceUrl ?? getTrackingSourceUrl());
  if (isInternalTestEvent) {
    markInternalAnalyticsDevice();
  } else {
    storeTrackingUserEmail(options?.userEmail);
  }
  const skip = new Set<SkippablePlatform>(options?.skipPlatforms ?? []);

  // Opted out outside an opt-in jurisdiction: pixels stay off, so conversion
  // events the pixels would have sent go to Meta/TikTok from the server under
  // Limited Data Use instead (track-event resolves the policy).
  let fanoutMode: FanoutMode | undefined = isInternalTestEvent ? 'none' : options?.fanoutMode;
  let fanoutTargets = options?.fanoutTargets;
  if (
    !isInternalTestEvent &&
    fanoutMode !== 'destinations' &&
    LIMITED_SERVER_FANOUT_EVENTS.has(eventName) &&
    isLimitedServerAdMode()
  ) {
    const limitedTargets = (['meta', 'tiktok'] as const).filter((target) => !skip.has(target));
    if (limitedTargets.length > 0) {
      fanoutMode = 'destinations';
      fanoutTargets = [...limitedTargets];
    }
  }

  // No ad tracking on teen/student guide pages or printables (the page the
  // visitor is on, not where they first landed). Covers SPA navigation into
  // these pages after the pixels loaded elsewhere. GA4 and PostHog are
  // unaffected (consent-gated).
  if (isCurrentPageAdExcluded()) {
    skip.add('meta');
    skip.add('tiktok');
    skip.add('google_ads');
    skip.add('appsflyer');
    fanoutMode = 'none';
    fanoutTargets = undefined;
  }

  // A device an internal account used never reaches Meta or TikTok, even
  // when signed out: no pixel, and no server fanout (track-event fans out to
  // Meta and TikTok only). Other destinations keep the email-based rules.
  if (isInternalAnalyticsDevice()) {
    skip.add('meta');
    skip.add('tiktok');
    fanoutMode = 'none';
    fanoutTargets = undefined;
  }

  const sendToServer = (posthogCaptured: boolean, analyticsOnly = false) =>
    trackToServer(
      eventName,
      posthogCaptured ? { ...(sanitizedParams || {}), posthog_client_captured: true } : sanitizedParams,
      options?.userId,
      analyticsOnly ? 'none' : fanoutMode,
      eventId,
      effectiveUserEmail,
      analyticsOnly ? undefined : fanoutTargets,
      options?.userFirstName,
      options?.userLastName,
      sourceUrl,
      options?.signupProof,
      analyticsOnly
    );

  if (isInternalTestEvent) {
    skip.add('ga4');
    skip.add('meta');
    skip.add('google_ads');
    skip.add('tiktok');
    skip.add('appsflyer');
    skip.add('posthog');
  }

  // Client-side tracking (if consent given and not server-only). Returns
  // whether PostHog took the event.
  const sendToClient = (): boolean => {
    const clientParams = {
      ...(sanitizedParams || {}),
      event_id: eventId,
    };
    let posthogCaptured = false;

    if (!skip.has('ga4')) trackToGA4(eventName, sanitizedParams, eventId);
    if (!skip.has('meta')) trackToMeta(eventName, sanitizedParams, eventId, effectiveUserEmail, options?.userId);
    if (!skip.has('google_ads')) {
      trackToGoogleAds(eventName, sanitizedParams, eventId, effectiveUserEmail, {
        kind: options?.googleAdsConversion,
        identity: {
          firstName: options?.userFirstName,
          lastName: options?.userLastName,
          phone: options?.userPhone,
          postalCode: options?.userZip,
          country: options?.userCountry,
        },
      });
    }
    if (!skip.has('tiktok')) trackToTikTok(eventName, clientParams, effectiveUserEmail);
    if (!skip.has('posthog')) posthogCaptured = trackToPostHog(eventName, sanitizedParams, eventId);
    if (!skip.has('appsflyer')) {
      void trackToAppsFlyer(eventName, sanitizedParams, eventId, {
        allowNativeWithoutConsent: options?.allowNativeAppsFlyerWithoutConsent,
      });
    }
    return posthogCaptured;
  };

  if (options?.serverFirst && !options.serverOnly && !options.clientOnly) {
    const answer = await sendToServer(false);
    const result = answer === 'not_sent' ? null : answer;
    options.onServerAnswer?.(
      answer === 'not_sent' ? 'not_sent' : !result ? 'failed' : result.deduped ? 'deduped' : 'stored'
    );
    if (result?.deduped) return;
    if (!result) {
      for (const platform of AD_AND_GA4_PLATFORMS) skip.add(platform);
    } else if (isNativeRuntime()) {
      // track-event already forwarded this native event to PostHog.
      skip.add('posthog');
    }
    sendToClient();
    return;
  }

  let posthogCaptured = false;
  if (!options?.serverOnly) posthogCaptured = sendToClient();

  // Server-side tracking (always attempt - handles ad blockers)
  if (!options?.clientOnly) {
    await sendToServer(posthogCaptured);
  } else if (
    isNativeRuntime() &&
    !options.serverOnly &&
    !posthogCaptured &&
    !skip.has('posthog') &&
    hasProductAnalyticsConsent()
  ) {
    // Most Android WebViews never hand events to posthog-js, so a
    // PostHog-only event (subscription_purchase and the other clientOnly
    // funnel events) would never reach PostHog. track-event forwards this
    // analytics-only copy to PostHog and does not store it or send it to
    // any ad platform.
    await sendToServer(false, true);
  }
}

export async function fanoutClientConversionDestinations(
  eventName: string,
  params: Record<string, unknown> | undefined,
  options: {
    eventId: string;
    userId?: string;
    userEmail?: string;
    userFirstName?: string;
    userLastName?: string;
    userCity?: string;
    userState?: string;
    userZip?: string;
    userCountry?: string;
    sourceUrl?: string;
    targets?: readonly Extract<FanoutTarget, 'meta' | 'tiktok'>[];
  }
): Promise<void> {
  if (isLocalWebTrackingEnvironment()) return;
  // An opted-out visitor outside an opt-in jurisdiction still gets the
  // server copy; the snapshot in eventData makes it Limited Data Use.
  if (!hasTrackingConsent() && !isLimitedServerAdMode()) return;
  if (!options.eventId) return;
  // Internal accounts never reach the ad platforms: the email sent, else the
  // stored tracking email, else a device an internal account used.
  if (isInternalAnalyticsTestUser(options.userEmail) || isInternalAnalyticsDevice()) return;
  // Same as trackEvent: nothing fires to Meta or TikTok while a teen/student
  // guide or printable is the current page.
  if (isCurrentPageAdExcluded()) return;

  try {
    const sanitizedParams = params ? sanitizeAnalyticsEventData(params) : undefined;
    let uid = options.userId;
    if (!uid) {
      const { data: { user } } = await supabase.auth.getUser();
      uid = user?.id;
    }

    const identifiers = getMarketingIdentifiers();
    const eventData = buildServerEventData(sanitizedParams);
    const sourceUrl = sanitizeTrackingSourceUrl(options.sourceUrl ?? getTrackingSourceUrl());
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
    const targets = options.targets ?? ['meta', 'tiktok'];

    const requests: Promise<unknown>[] = [];
    if (targets.includes('meta')) {
      await waitForMetaPixelEventSent(eventName, options.eventId);

      requests.push(
        supabase.functions.invoke('meta-conversions-api', {
          body: {
            eventName,
            eventId: options.eventId,
            eventData,
            userId: uid,
            userEmail: options.userEmail,
            userFirstName: normalizeTrackingName(options.userFirstName),
            userLastName: normalizeTrackingName(options.userLastName),
            userCity: normalizeTrackingName(options.userCity),
            userState: normalizeTrackingName(options.userState),
            userZip: normalizeTrackingName(options.userZip),
            userCountry: normalizeTrackingName(options.userCountry),
            fbp: identifiers.fbp,
            fbc: identifiers.fbc,
            sourceUrl,
            userAgent,
          },
        })
      );
    }

    if (targets.includes('tiktok')) {
      requests.push(
        supabase.functions.invoke('tiktok-events-api', {
          body: {
            eventName,
            eventId: options.eventId,
            eventData,
            userId: uid,
            userEmail: options.userEmail,
            userFirstName: normalizeTrackingName(options.userFirstName),
            userLastName: normalizeTrackingName(options.userLastName),
            userCity: normalizeTrackingName(options.userCity),
            userState: normalizeTrackingName(options.userState),
            userZip: normalizeTrackingName(options.userZip),
            userCountry: normalizeTrackingName(options.userCountry),
            ttclid: identifiers.ttclid,
            ttp: identifiers.ttp,
            sourceUrl,
            userAgent,
          },
        })
      );
    }

    await Promise.allSettled(requests);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('Client conversion destination fanout failed:', error);
    }
  }
}

let lastTrackedPageView:
  | {
      key: string;
      trackedAt: number;
    }
  | null = null;

let lastTrackedCheckoutStarted:
  | {
      key: string;
      trackedAt: number;
    }
  | null = null;

function getCheckoutStartedDedupeKey(params: Record<string, unknown> | undefined): string {
  const tier = params?.tier ?? '';
  const provider = params?.provider ?? '';
  const source = params?.source ?? '';
  const paywallType = params?.paywall_type ?? '';
  const offerKind = params?.offer_kind ?? '';
  return [tier, provider, source, paywallType, offerKind].join('|');
}

/**
 * One `checkout_started` per real attempt. Rapid double-fires from the same
 * CTA (re-render, double tap before busy state, panel + startCheckout both
 * emitting) collapse into one. A later attempt after the window, or a
 * different plan/provider/offer, still fires.
 *
 * sessionStorage is the source of truth for the short window. In-memory only
 * backs up when storage is unavailable, so clearing session storage (tests,
 * a new tab session) cannot leave a stale in-memory block behind.
 */
function shouldSkipDuplicateCheckoutStarted(
  params: Record<string, unknown> | undefined,
): boolean {
  if (typeof window === 'undefined') return false;

  const now = Date.now();
  const key = getCheckoutStartedDedupeKey(params);

  try {
    const storageKey = `${CHECKOUT_STARTED_SESSION_DEDUPE_PREFIX}${key}`;
    const previous = Number(window.sessionStorage.getItem(storageKey) || 0);
    if (
      Number.isFinite(previous) &&
      previous > 0 &&
      now - previous < CHECKOUT_STARTED_DEDUPE_WINDOW_MS
    ) {
      return true;
    }
    window.sessionStorage.setItem(storageKey, String(now));
    lastTrackedCheckoutStarted = { key, trackedAt: now };
    return false;
  } catch {
    // Storage unavailable: same-tick in-memory guard only.
  }

  if (
    lastTrackedCheckoutStarted?.key === key &&
    now - lastTrackedCheckoutStarted.trackedAt < CHECKOUT_STARTED_DEDUPE_WINDOW_MS
  ) {
    return true;
  }
  lastTrackedCheckoutStarted = { key, trackedAt: now };
  return false;
}

function getPageViewDedupeKey(path: string, pageCategory: string): string {
  return `${pageCategory}|${path}`;
}

function getPageViewSessionDedupeStorageKey(key: string): string {
  return `${PAGE_VIEW_SESSION_DEDUPE_PREFIX}${key}`;
}

function getPageViewSessionDedupeWindowMs(pageCategory: string): number {
  return shouldPersistentlyDedupePageView(pageCategory)
    ? Number.POSITIVE_INFINITY
    : PAGE_VIEW_SESSION_DEDUPE_WINDOW_MS;
}

function getFunnelPageViewPersistentDedupeStorageKey(key: string): string {
  return `${FUNNEL_PAGE_VIEW_PERSISTENT_DEDUPE_PREFIX}${key}`;
}

function shouldPersistentlyDedupePageView(pageCategory: string): boolean {
  return pageCategory === 'funnel' || pageCategory === 'technical';
}

function getPageViewSkipPlatforms(options?: PageViewOptions): readonly SkippablePlatform[] | undefined {
  const skipPlatforms = new Set<SkippablePlatform>(options?.skipPlatforms ?? []);

  if (
    options?.pageCategory === 'technical' ||
    options?.pageCategory === 'admin' ||
    options?.isAdminPage
  ) {
    INTERNAL_PAGE_VIEW_SKIP_PLATFORMS.forEach((platform) => skipPlatforms.add(platform));
  }

  return skipPlatforms.size > 0 ? Array.from(skipPlatforms) : undefined;
}

function shouldSkipDuplicatePageView(path: string, pageCategory: string): boolean {
  if (typeof window === 'undefined') return false;

  const now = Date.now();
  const key = getPageViewDedupeKey(path, pageCategory);
  if (lastTrackedPageView?.key === key && now - lastTrackedPageView.trackedAt < PAGE_VIEW_DEDUPE_WINDOW_MS) {
    return true;
  }

  try {
    const storageKey = getPageViewSessionDedupeStorageKey(key);
    const previous = Number(window.sessionStorage.getItem(storageKey) || 0);
    const sessionDedupeWindowMs = getPageViewSessionDedupeWindowMs(pageCategory);
    if (
      Number.isFinite(previous) &&
      previous > 0 &&
      now - previous < sessionDedupeWindowMs
    ) {
      lastTrackedPageView = { key, trackedAt: now };
      return true;
    }
    window.sessionStorage.setItem(storageKey, String(now));
  } catch {
    // If storage is unavailable, the in-memory guard still covers normal SPA
    // duplicate renders.
  }

  if (shouldPersistentlyDedupePageView(pageCategory) && hasTrackingConsent()) {
    try {
      const storageKey = getFunnelPageViewPersistentDedupeStorageKey(key);
      const previous = Number(window.localStorage.getItem(storageKey) || 0);
      if (
        Number.isFinite(previous) &&
        previous > 0 &&
        now - previous < FUNNEL_PAGE_VIEW_PERSISTENT_DEDUPE_WINDOW_MS
      ) {
        lastTrackedPageView = { key, trackedAt: now };
        return true;
      }
      window.localStorage.setItem(storageKey, String(now));
    } catch {
      // Session and in-memory guards still handle the common duplicate paths.
    }
  }

  lastTrackedPageView = { key, trackedAt: now };
  return false;
}

/**
 * Track page view
 */
export function trackPageView(
  pagePath?: string,
  pageTitle?: string,
  options?: PageViewOptions
): void {
  const rawPath = pagePath || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const path = sanitizeTrackingUrl(rawPath, 'path');
  const title = pageTitle || (typeof document !== 'undefined' ? document.title : '');
  const pageCategory = options?.pageCategory ?? 'public';
  const isAdminPage = options?.isAdminPage ?? false;
  const pageLocation =
    typeof window !== 'undefined'
      ? normalizeUrlForGa4Attribution(sanitizeTrackingUrl(window.location.href, 'absolute'))
      : undefined;

  if (shouldSkipDuplicatePageView(path, pageCategory)) return;

  const resolvedSkipPlatforms = getPageViewSkipPlatforms(options);
  const skipPlatforms = new Set<SkippablePlatform>(resolvedSkipPlatforms ?? []);
  const shouldFanoutMetaPageView = !skipPlatforms.has('meta');

  trackEvent('page_view', {
    page_path: path,
    page_location: pageLocation,
    page_title: title,
    page_category: pageCategory,
    is_admin_page: isAdminPage,
  }, {
    skipPlatforms: resolvedSkipPlatforms,
    sourceUrl: pageLocation,
    fanoutMode: shouldFanoutMetaPageView ? 'destinations' : 'none',
    fanoutTargets: shouldFanoutMetaPageView ? ['meta'] : undefined,
  });
}

/**
 * Track conversion event (purchases, signups, etc.)
 */
export async function trackConversion(
  eventName: string,
  data: ConversionData,
  userId?: string,
  options?: Omit<TrackEventOptions, 'userId'>
): Promise<void> {
  await trackEvent(eventName, data, {
    ...options,
    ...(userId ? { userId } : {}),
  });
}

/**
 * Set user ID for tracking (call after login)
 */
export function setTrackingUserId(userId: string, userEmail?: string): void {
  if (!userId) return;
  noteActiveAdIdentityUser(userId);
  // Before the consent check: an opted-out device is exactly the case whose
  // choice must reach the account.
  syncAdDataChoiceForSignedInUser(userId);

  // Ad identities wait for the account's opt-out (a new device must not tie
  // this member to GA4/Meta/TikTok/AppsFlyer before it knows). Already read
  // this session: set up now. A read error or timeout skips them for this
  // session. Product analytics identification does not go through here.
  const cached = getCachedAccountAdDataOptOut(userId);
  if (cached === false) {
    configureAdIdentity(userId, userEmail);
    return;
  }
  void resolveAdIdentityPermission(userId).then((permission) => {
    if (activeAdIdentityUserId !== userId) return;
    if (permission !== 'allowed') {
      // Opted out or unknown: nothing about this member, or anyone before
      // them, is handed to ad platforms this session.
      clearAdIdentityState();
      return;
    }
    configureAdIdentity(userId, userEmail);
  });
}

function configureAdIdentity(userId: string, userEmail?: string): void {
  if (!hasTrackingConsent()) return;
  if (hasExplicitDeviceAdOptOut()) return;
  const normalizedUserId = normalizeMetaExternalId(userId);
  if (!normalizedUserId) return;

  // Native builds use Firebase app streams; browsers use the web data stream.
  if (isNativeRuntime()) {
    void setNativeFirebaseAnalyticsUserId(normalizedUserId);
  } else if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', { user_id: normalizedUserId });
  }

  // Meta Pixel - associate user
  setMetaAdvancedMatchingData({
    em: normalizeMetaAdvancedMatchingEmail(userEmail),
    external_id: normalizedUserId,
  });
  setTikTokAdvancedMatchingData({
    email: normalizeMetaAdvancedMatchingEmail(userEmail),
    external_id: normalizedUserId,
  });
  if (typeof window !== 'undefined' && window.AF) {
    window.AF('pba', 'setCustomerUserId', normalizedUserId);
  }

  // Store for server-side tracking
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('tracking_user_id', normalizedUserId);
    const normalizedEmail = normalizeMetaAdvancedMatchingEmail(userEmail);
    if (normalizedEmail) {
      localStorage.setItem('tracking_user_email', normalizedEmail);
    }
  }
}

function normalizedGa4PagePath(path: string): string {
  return sanitizeTrackingUrl(path, 'path').replace(/\/+$/, '') || '/';
}

function notifyTrackingConsent(status: TrackingConsentStatus) {
  if (typeof window === 'undefined') return;

  if (status === 'denied') clearPendingGa4Tracking();
  if (status === 'granted' && hasTrackingConsent() && pendingGa4PageView) {
    const pending = pendingGa4PageView;
    pendingGa4PageView = null;
    const path = String(pending.params.page_path);
    if (normalizedGa4PagePath(path) === normalizedGa4PagePath(window.location.pathname)) {
      trackToGA4('page_view', {
        ...pending.params,
        page_location: normalizeUrlForGa4Attribution(sanitizeTrackingUrl(window.location.href, 'absolute')),
      }, pending.eventId, 0, path);
    }
  }

  window.dispatchEvent(
    new CustomEvent(TRACKING_CONSENT_EVENT, {
      detail: { status },
    })
  );
}

function updateClientSideConsent(status: 'granted' | 'denied'): void {
  // Ad pixels never load on teen/student guides (full page load + landing
  // filter in MarketingScripts); GA4 there keeps ad consent denied even when
  // the visitor grants consent on that page.
  const adStatus =
    isCurrentPageAdExcluded() ? 'denied' : status;
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: status,
      ad_storage: adStatus,
      ad_user_data: adStatus,
      ad_personalization: adStatus,
    });
  }

  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('consent', status === 'granted' ? 'grant' : 'revoke');
  }
}

function applyNativeTrackingConsentSideEffects(status: TrackingConsentStatus): void {
  void syncNativeFirebaseAnalyticsConsent(status);

  if (status === 'granted' || status === 'denied') {
    updateClientSideConsent(status);
  }

  if (status === 'denied') {
    clearStoredTrackingUserIdentifiers();
  }

  // No-op while the iOS ATT answer is still pending.
  void flushPendingAppsFlyerAttEvents();
}

export function syncNativeTrackingConsentStatus(status: TrackingConsentStatus): boolean {
  const previousNativeStatus = getNativeTrackingConsentStatus();
  const previousEffectiveStatus = getTrackingConsentStatus();
  persistNativeTrackingConsent(status);
  const nextEffectiveStatus = getTrackingConsentStatus();
  applyNativeTrackingConsentSideEffects(nextEffectiveStatus);

  if (previousEffectiveStatus !== nextEffectiveStatus) {
    notifyTrackingConsent(nextEffectiveStatus);
  }

  return previousNativeStatus !== status;
}

/**
 * Fires the consent event when either the advertising status or the product
 * analytics status changed. On iOS an opt-out (or Allow) in Your Privacy
 * Choices can leave the ATT-combined status unchanged ('denied' after an ATT
 * decline) while product analytics flips, and PostHog must still hear it.
 */
function notifyIfConsentChanged(
  previousEffectiveStatus: TrackingConsentStatus,
  previousProductStatus: ReturnType<typeof getProductAnalyticsStatus>
): void {
  const nextEffectiveStatus = getTrackingConsentStatus();
  if (
    previousEffectiveStatus !== nextEffectiveStatus ||
    previousProductStatus !== getProductAnalyticsStatus()
  ) {
    notifyTrackingConsent(nextEffectiveStatus);
  }
}

interface TrackingChoiceOptions {
  /** Record the choice on the signed-in account (default true). */
  syncAccount?: boolean;
  /** Where the ad data choice was made, stored on the account. */
  adDataSource?: Exclude<AdDataOptOutSource, 'gpc'>;
}

const AD_DATA_OPT_OUT_SOURCE_KEY = 'jp_ad_data_opt_out_source';

function defaultAdDataSource(): Exclude<AdDataOptOutSource, 'gpc'> {
  return isNativeRuntime() ? 'native' : 'privacy_choices';
}

function persistAdDataOptOutSource(source: Exclude<AdDataOptOutSource, 'gpc'> | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (source) window.localStorage.setItem(AD_DATA_OPT_OUT_SOURCE_KEY, source);
    else window.localStorage.removeItem(AD_DATA_OPT_OUT_SOURCE_KEY);
  } catch {
    // Storage blocked: sign-in falls back to the default source.
  }
}

function readAdDataOptOutSource(): Exclude<AdDataOptOutSource, 'gpc'> {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(AD_DATA_OPT_OUT_SOURCE_KEY);
      if (stored === 'banner' || stored === 'privacy_choices' || stored === 'native') return stored;
    } catch {
      // Fall through to the default.
    }
  }
  return defaultAdDataSource();
}

function readStorageValue(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * An opt-out the person made on this device: the banner Decline, Your
 * Privacy Choices, the app's toggle. Never the default for a region, never
 * an ATT decline, never an opt-out applied from an account.
 */
function hasExplicitDeviceAdOptOut(): boolean {
  if (isNativeIosRuntime()) return readStorageValue(NATIVE_APP_TRACKING_OPTOUT_KEY) === '1';
  if (isNativeRuntime()) {
    if (readStorageValue(NATIVE_TRACKING_CONSENT_KEY) === 'denied') return true;
  } else if (readCookie(SHARED_TRACKING_CONSENT_COOKIE) === 'denied') {
    return true;
  }
  return (
    readStorageValue('tracking_consent') === 'denied' ||
    readStorageValue('cookie_consent_status') === 'declined'
  );
}

/**
 * This device's ad opt-out, as the account should record it (null = none).
 * Only GPC and explicit choices on this device count.
 */
function getDeviceAdDataOptOutSource(): AdDataOptOutSource | null {
  if (hasGlobalPrivacyControlSignal()) return 'gpc';
  return hasExplicitDeviceAdOptOut() ? readAdDataOptOutSource() : null;
}

/**
 * Sign-in: a GPC signal or an explicit opt-out on this device is recorded on
 * the account (set_my_ad_data_opt_out), so server-side sends honor it.
 */
function syncAdDataChoiceForSignedInUser(userId: string): void {
  if (!userId) return;
  startAccountAdDataSyncRetry();
  const source = getDeviceAdDataOptOutSource();
  void syncDeviceAdDataOptOutToAccount(userId, {
    gpc: source === 'gpc',
    optOutSource: source === 'gpc' ? null : source,
  }).catch(() => undefined);
}

/**
 * The ad data opt-out as this device knows it right now, without a network
 * read: GPC, an explicit choice here, or an account opt-out applied for the
 * member signed in (or the last one, when nobody is). ATT is not consulted.
 */
export function isAdDataOptedOutOnDevice(): boolean {
  return hasGlobalPrivacyControlSignal() || hasExplicitDeviceAdOptOut() || hasAccountAdDataOptOut();
}

export type AdIdentityPermission = 'allowed' | 'opted_out' | 'unknown';

/**
 * Whether ad identities (GA4/Meta/TikTok/AppsFlyer user ids, RevenueCat's
 * AppsFlyer id) may be set up for this member on this device. Opted out: GPC,
 * an explicit opt-out on this device, or the member's account opt-out
 * (applied to the device when read). 'unknown': the account could not be
 * read within the timeout; callers skip ad identity setup for this session
 * without persisting an opt-out. An ATT decline alone is not an opt-out.
 */
export async function resolveAdIdentityPermission(
  userId: string,
  timeoutMs = AD_ACCOUNT_READ_TIMEOUT_MS
): Promise<AdIdentityPermission> {
  if (!userId) return 'unknown';
  noteActiveAdIdentityUser(userId);
  // A device opt-out answers at once, even offline; the account read still
  // runs (to apply the account value and settle a signed-out choice).
  if (hasGlobalPrivacyControlSignal() || hasExplicitDeviceAdOptOut()) {
    void resolveAccountAdDataOptOut(userId, () => applyAccountAdDataOptOut(userId), timeoutMs).catch(
      () => 'unknown'
    );
    return 'opted_out';
  }
  const resolution = await resolveAccountAdDataOptOut(
    userId,
    () => applyAccountAdDataOptOut(userId),
    timeoutMs
  );
  if (hasGlobalPrivacyControlSignal() || hasExplicitDeviceAdOptOut()) return 'opted_out';
  if (resolution === 'opted_out' || readAccountAdDataOptOutOwner() === userId) return 'opted_out';
  return resolution === 'not_opted_out' ? 'allowed' : 'unknown';
}

/**
 * The signed-in account opted out of ad data sharing (on another device or
 * through support): put this device in the state a local Decline produces
 * for ads. Pixels and native ad SDK sends stop; on web outside an opt-in
 * region, conversions still go server-side under Limited Data Use. Product
 * analytics is untouched (it has its own account flag). Persisted for that
 * member, so the next page load and a signed-out session start opted out.
 */
function applyAccountAdDataOptOut(userId: string): void {
  if (readAccountAdDataOptOutOwner() === userId) return;
  const previousEffectiveStatus = getTrackingConsentStatus();
  persistAccountAdDataOptOut(userId);
  const nextEffectiveStatus = getTrackingConsentStatus();

  if (isNativeRuntime()) {
    applyNativeTrackingConsentSideEffects(nextEffectiveStatus);
  } else {
    clearStoredTrackingUserIdentifiers();
    updateClientSideConsent('denied');
  }

  if (previousEffectiveStatus !== nextEffectiveStatus) {
    notifyTrackingConsent(nextEffectiveStatus);
  }
}


/**
 * Grant tracking consent
 */
export function grantTrackingConsent(options?: TrackingChoiceOptions): void {
  if (options?.syncAccount !== false) {
    void pushAccountAnalyticsOptOut(false);
    // An explicit opt-in clears the account's ad opt-out, except on a GPC
    // browser: GPC always wins for this browser.
    if (!hasGlobalPrivacyControlSignal()) {
      void pushAccountAdDataOptOut(false, options?.adDataSource ?? defaultAdDataSource()).catch(() => false);
    }
  }
  persistAdDataOptOutSource(null);

  const previousEffectiveStatus = getTrackingConsentStatus();
  const previousProductStatus = getProductAnalyticsStatus();

  // An explicit Accept here lifts an applied account opt-out on this device;
  // a GPC browser stays opted out (and the account keeps its opt-out).
  if (!hasGlobalPrivacyControlSignal() && hasAccountAdDataOptOut()) persistAccountAdDataOptOut(null);

  if (isNativeIosRuntime()) {
    persistNativeAppTrackingOptOut(false);
    persistNativeAnalyticsExplicitGrant(true);
    applyNativeTrackingConsentSideEffects(getTrackingConsentStatus());
    notifyIfConsentChanged(previousEffectiveStatus, previousProductStatus);
    return;
  }

  if (isNativeRuntime()) {
    persistNativeAppTrackingOptOut(false);
    persistNativeAnalyticsExplicitGrant(true);
    persistNativeTrackingConsent('granted');
    applyNativeTrackingConsentSideEffects(getTrackingConsentStatus());
    notifyIfConsentChanged(previousEffectiveStatus, previousProductStatus);
    return;
  }

  persistLegacyTrackingConsent('granted');
  persistSharedTrackingConsent('granted');
  updateClientSideConsent('granted');

  notifyTrackingConsent('granted');
}

/**
 * Revoke tracking consent. Also the product analytics opt-out: PostHog and
 * server forwarding stop for this device (and the account when signed in).
 */
export function revokeTrackingConsent(options?: TrackingChoiceOptions): void {
  const adDataSource = options?.adDataSource ?? defaultAdDataSource();
  persistAdDataOptOutSource(adDataSource);
  if (options?.syncAccount !== false) {
    void pushAccountAnalyticsOptOut(true);
    void pushAccountAdDataOptOut(true, hasGlobalPrivacyControlSignal() ? 'gpc' : adDataSource).catch(() => false);
  }

  const previousEffectiveStatus = getTrackingConsentStatus();
  const previousProductStatus = getProductAnalyticsStatus();

  if (isNativeIosRuntime()) {
    persistNativeAppTrackingOptOut(true);
    persistNativeAnalyticsExplicitGrant(false);
    applyNativeTrackingConsentSideEffects('denied');
    notifyIfConsentChanged(previousEffectiveStatus, previousProductStatus);
    return;
  }

  if (isNativeRuntime()) {
    persistNativeAppTrackingOptOut(true);
    persistNativeAnalyticsExplicitGrant(false);
    persistNativeTrackingConsent('denied');
    applyNativeTrackingConsentSideEffects(getTrackingConsentStatus());
    notifyIfConsentChanged(previousEffectiveStatus, previousProductStatus);
    return;
  }

  persistLegacyTrackingConsent('denied');
  persistSharedTrackingConsent('denied');

  clearStoredTrackingUserIdentifiers();

  updateClientSideConsent('denied');

  notifyTrackingConsent('denied');
}

// ============================================
// Pre-defined Business Events
// ============================================

export const BusinessEvents = {
  // Authentication
  signUp: (
    method: 'email' | 'google' | 'apple',
    email?: string,
    identity?: {
      userId?: string;
      userFirstName?: string;
      userLastName?: string;
      signupProof?: string;
    },
    // 'guest_upgrade': a native guest (anonymous account from onboarding)
    // connected an email, Google or Apple. `method` stays the provider so
    // GA4 and ad destinations keep their standard values.
    // serverFirst (needs identity.userId): one event id per account on every
    // device, and track-event decides before any client destination fires,
    // so a second device that sees the same new account sends nothing.
    details?: { flow?: 'guest_upgrade'; serverFirst?: boolean }
    // Resolves to track-event's server-first answer, or undefined when the
    // event was not sent server first (or not tracked at all).
  ): Promise<ServerFirstAnswer | undefined> => {
    // Opt in to destination fanout so Meta CAPI receives the standard
    // CompleteRegistration event for users whose browser-side Meta pixel is
    // blocked. For Meta, Lead stays an email-capture-only event; for Google
    // Ads, sign_up fires the Lead conversion because the account's Sign-up
    // goal is defined as account creations (leads-first bidding strategy).
    const options: TrackEventOptions = {
      fanoutMode: 'destinations',
      fanoutTargets: ['meta', 'tiktok'],
      ...(email ? { userEmail: email } : {}),
      ...(identity?.userId ? { userId: identity.userId } : {}),
      ...(identity?.userFirstName ? { userFirstName: identity.userFirstName } : {}),
      ...(identity?.userLastName ? { userLastName: identity.userLastName } : {}),
      ...(identity?.signupProof ? { signupProof: identity.signupProof } : {}),
    };
    const data = { method, ...(details?.flow ? { signup_flow: details.flow } : {}) };
    if (details?.serverFirst && identity?.userId) {
      let answer: ServerFirstAnswer | undefined;
      return createAccountScopedEventId('sign_up', identity.userId)
        .then((eventId) =>
          trackConversion('sign_up', data, undefined, {
            ...options,
            eventId,
            serverFirst: true,
            onServerAnswer: (result) => {
              answer = result;
            },
          })
        )
        .then(() => answer);
    }
    return trackConversion('sign_up', data, undefined, options).then(() => undefined);
  },
  
  login: (method: 'email' | 'google' | 'apple') =>
    trackEvent('login', { method }),

  // Subscription
  //
  // `trial_started` and `subscription_purchase` are intentionally NOT exposed
  // here. Both events fire server-side only from stripe-webhook-handler /
  // paypal-webhook-handler on the real billing-provider signal -- that's the
  // only source of truth. Adding a client-side helper tempts callers to fire
  // these on success screens, which historically caused:
  //   - Duplicate events when the webhook also fired (no shared event_id).
  //   - Premature or false conversions when the user reached the success
  //     screen but the charge later failed / 3DS declined.
  //   - Broken CAPI fanout: the old helpers didn't pass
  //     `fanoutMode: 'destinations'`, so Meta/TikTok never received them.
  // If you need a post-purchase client-side marker, fire a custom UX event
  // instead (e.g. `premium_welcome_viewed`) and keep the standard
  // conversion events where they are: in the webhook.

  subscriptionCanceled: (plan: string, reason?: string) =>
    trackEvent('subscription_canceled', { plan, reason }),

  cancelFlowOpened: () =>
    trackEvent('cancel_flow_opened'),

  cancelReasonSubmitted: (reason: string) =>
    trackEvent('cancel_reason_submitted', { reason }),

  cancelFlowKept: () =>
    trackEvent('cancel_flow_kept'),

  saveOfferShown: (offer: string, reason: string) =>
    trackEvent('save_offer_shown', { offer, reason }),

  saveOfferAccepted: (offer: string, reason: string) =>
    trackEvent('save_offer_accepted', { offer, reason }),

  saveOfferDeclined: (offer: string, reason: string) =>
    trackEvent('save_offer_declined', { offer, reason }),

  // Content Engagement
  programStarted: (programId: string, programName: string, isFree: boolean) => 
    trackEvent('program_started', { program_id: programId, program_name: programName, is_free: isFree }),
  
  programCompleted: (programId: string, programName: string, durationMinutes: number) => 
    trackEvent('program_completed', { program_id: programId, program_name: programName, duration_minutes: durationMinutes }),
  
  eventJoined: (eventId: string, eventName: string, isLive: boolean) => 
    trackEvent('event_joined', { event_id: eventId, event_name: eventName, is_live: isLive }),
  
  eventCompleted: (eventId: string, eventName: string, attendeeCount: number) => 
    trackEvent('event_completed', { event_id: eventId, event_name: eventName, attendee_count: attendeeCount }),

  // Creator Actions
  programCreated: (programId: string, visibility: 'private' | 'public' | 'unlisted') => 
    trackEvent('program_created', { program_id: programId, visibility }),
  
  eventCreated: (eventId: string, hasVideo: boolean) => 
    trackEvent('event_created', { event_id: eventId, has_video: hasVideo }),

  // Social
  userFollowed: (followedUserId: string) => 
    trackEvent('user_followed', { followed_user_id: followedUserId }),
  
  contentShared: (
    contentType: string,
    contentId: string,
    platform: string,
    page?: string
  ) =>
    trackEvent('content_shared', {
      content_type: contentType,
      content_id: contentId,
      platform,
      page,
    }),

  // Engagement
  streakAchieved: (streakDays: number) => 
    trackEvent('streak_achieved', { streak_days: streakDays }),
  
  badgeEarned: (badgeId: string, badgeName: string) => 
    trackEvent('badge_earned', { badge_id: badgeId, badge_name: badgeName }),
};

export default {
  trackEvent,
  trackPageView,
  trackConversion,
  getTrackingConsentStatus,
  setTrackingUserId,
  grantTrackingConsent,
  revokeTrackingConsent,
  clearTrackingConsentPersistence,
  syncNativeTrackingConsentStatus,
  BusinessEvents,
};
